import React, { useState, useMemo, useRef, useEffect } from 'react';
import { ServerStatusNotice } from '@/components/common/ServerStatusNotice';
import { createPortal } from 'react-dom';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useRememberFilters } from '@/hooks/useRememberFilters';
import useSWR from 'swr';
import { Loader2, X, PenTool, Trash2, AlertTriangle, CameraOff } from 'lucide-react';
import { contours as d3Contours } from 'd3-contour';
import { ZoneDropdown } from '@/components/layout/ZoneDropdown';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from 'recharts';

import { Select } from '@/components/ui/select';
import { useT } from '@/contexts/I18nContext';
import { useCurrentZone } from '@/hooks/useCurrentZone';
import { BranchRequiredState } from '@/components/common/BranchRequiredState';
import { useCan } from '@/hooks/useCan';
import {
  getSeatingStats,
  getSeatingTimeofday,
  getCameraDensityGrid,
  getCellTimeOfDay,
  getHeatmapProgress,
  getSeatingProgress,
  type SeatingPolygon,
  type DensityGridDto,
  type HotZoneDto,
  type HeatmapMode,
  type CellTimeOfDayDto,
  type DailyProgressDto,
  type SeatingWeekdays,
} from '@/api/seating';
import { listAreasByZone } from '@/api/areas';
import { listCameras } from '@/api/cameras';
import { getLatestFrame } from '@/api/frames';
import { toAbsoluteUrl } from '@/api/url';
import { API_HOST } from '@/api/base';
import type { ZoneWorkingHoursDto } from '@/api/workingHours';
import type { ZoneType } from '@/api/zones';
import { getWeekdayLabels } from '@/components/working-hours/weekdays';
import { Tooltip as UiTooltip } from '@/components/ui/tooltip';

type Period = '7d' | '30d' | '8we' | '10wd';
const ALL_PERIODS: Period[] = ['7d', '30d', '8we', '10wd'];
type WeekdayFilter = 'all' | 'weekend' | 'workday';

/** Branch types the compare-zone dropdown should expose. Mirrors
 *  `BranchSelector`'s RETAIL_TYPES so the heatmap compare list
 *  matches the global branch picker — no service/system zones
 *  showing up in compare that aren't selectable as the main branch. */
const COMPARE_RETAIL_TYPES: ZoneType[] = [
  'RETAIL_BRANCH', 'COFFEESHOP', 'SHOP', 'CANTEEN',
];

/** For non-contiguous periods (8 weekend days, 10 workdays) we pick a
 *  from/to range that contains exactly N matching days, then ask the
 *  BE to subset via `weekdays=`. Cleaner than an ?dates=… list and
 *  reuses the existing per-day cache. */
function periodWeekdayFilter(period: Period): WeekdayFilter {
  if (period === '8we') return 'weekend';
  if (period === '10wd') return 'workday';
  return 'all';
}
/**
 * Single-choice layer mode. Frame is always rendered (the camera image is
 * baseline context, not a toggleable layer). Mode picks the analytical
 * overlay shown on top of the frame:
 *   - 'zones'    → annotated polygons (tables, staff zones)
 *   - 'density'  → smooth Spectral_r gradient + hover dot + per-point hours/day
 *   - 'hotzones' → server-side density-CC clusters with per-cluster integrals
 */
type LayerMode = 'zones' | 'heatmap' | 'hotzones';
const DEFAULT_LAYER_MODE: LayerMode = 'zones';
const ALL_LAYER_MODES: LayerMode[] = ['zones', 'heatmap', 'hotzones'];
/** Modes shown to regular users. 'hotzones' is admin-only as of
 *  2026-05-06 — the radius-based clustering math hasn't been ported
 *  to the simpler ellipse model yet, so it can confuse end-users.
 *  Free-draw (inside 'heatmap' mode) is similarly admin-only. */
const PUBLIC_LAYER_MODES: LayerMode[] = ['zones', 'heatmap'];

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function periodDates(period: Period): { from: string; to: string } {
  // "Last N full days" — never includes today. Today is partial data
  // (frames still arriving) and isn't comparable to completed days.
  // The window's `to` is yesterday; `from` walks back from there.
  const to = new Date();
  to.setDate(to.getDate() - 1);
  if (period === '7d' || period === '30d') {
    const days = period === '7d' ? 7 : 30;
    const from = new Date(to);
    // (days - 1) so the inclusive window has exactly `days` calendar
    // dates: yesterday minus (days - 1) → yesterday.
    from.setDate(from.getDate() - (days - 1));
    return { from: toIsoDate(from), to: toIsoDate(to) };
  }
  // For non-contiguous periods we pick a from/to that contains exactly
  // the requested count of matching weekdays. The BE filters the range
  // via the `weekdays` query param, so we don't have to send a list of
  // dates.
  if (period === '8we') {
    // Walk back from yesterday, counting weekend days, until we hit 8.
    const cur = new Date(to);
    let weekendCount = 0;
    while (weekendCount < 8) {
      const dow = cur.getDay();
      if (dow === 0 || dow === 6) weekendCount += 1;
      if (weekendCount >= 8) break;
      cur.setDate(cur.getDate() - 1);
    }
    return { from: toIsoDate(cur), to: toIsoDate(to) };
  }
  // '10wd' — same logic for workdays.
  const cur = new Date(to);
  let workdayCount = 0;
  while (workdayCount < 10) {
    const dow = cur.getDay();
    if (dow >= 1 && dow <= 5) workdayCount += 1;
    if (workdayCount >= 10) break;
    cur.setDate(cur.getDate() - 1);
  }
  return { from: toIsoDate(cur), to: toIsoDate(to) };
}

function toUnixMs(dateStr: string): number {
  return new Date(dateStr).getTime();
}

// 11-stop Spectral_r LUT (matplotlib Spectral reversed: blue=low, red=high)
const SPECTRAL_R_STOPS: [number, [number, number, number]][] = [
  [0.0, [0x5e, 0x4f, 0xa2]],
  [0.1, [0x32, 0x88, 0xbd]],
  [0.2, [0x66, 0xc2, 0xa5]],
  [0.3, [0xab, 0xdd, 0xa4]],
  [0.4, [0xe6, 0xf5, 0x98]],
  [0.5, [0xff, 0xff, 0xbf]],
  [0.6, [0xfe, 0xe0, 0x8b]],
  [0.7, [0xfd, 0xae, 0x61]],
  [0.8, [0xf4, 0x6d, 0x43]],
  [0.9, [0xd5, 0x3e, 0x4f]],
  [1.0, [0x9e, 0x01, 0x42]],
];

function spectralRColor(t: number): [number, number, number] {
  const v = Math.max(0, Math.min(1, t));
  for (let i = 0; i < SPECTRAL_R_STOPS.length - 1; i++) {
    const [t0, c0] = SPECTRAL_R_STOPS[i];
    const [t1, c1] = SPECTRAL_R_STOPS[i + 1];
    if (v <= t1) {
      const f = (v - t0) / (t1 - t0);
      return [
        Math.round(c0[0] + f * (c1[0] - c0[0])),
        Math.round(c0[1] + f * (c1[1] - c0[1])),
        Math.round(c0[2] + f * (c1[2] - c0[2])),
      ];
    }
  }
  return SPECTRAL_R_STOPS[SPECTRAL_R_STOPS.length - 1][1];
}

/** Track an element's rendered width via ResizeObserver. The SVG's
 *  viewBox is in image pixels, so anything sized in viewBox units
 *  (the cursor dot, mostly) gets scaled with the displayed size —
 *  tiny in the multi-tile grid view, big in the fullscreen modal.
 *  Use the rendered width to compute a constant SCREEN size.
 *
 *  `signal` is a value that, when it changes, re-runs the effect.
 *  Pass something that becomes truthy once the observed element is
 *  in the DOM (e.g. `imgDims?.w`) — the SVG mounts conditionally on
 *  the image loading, so the initial effect run sees `ref.current ===
 *  null` and would otherwise never retry. */
function useElementWidth(ref: React.RefObject<Element | null>, signal?: unknown): number {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setWidth(el.getBoundingClientRect().width);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, signal]);
  return width;
}

/** Seconds elapsed since `active` flipped to true. Resets to 0 when
 *  `active` becomes false. Powers the "Loading… 23s" overlay so a
 *  user staring at a frozen-looking 30-day cold-compute knows
 *  something is actually happening and roughly how much they've
 *  already waited. */
function useElapsedSeconds(active: boolean): number {
  const [secs, setSecs] = useState(0);
  useEffect(() => {
    if (!active) {
      setSecs(0);
      return;
    }
    const t0 = Date.now();
    setSecs(0);
    const id = window.setInterval(() => {
      setSecs(Math.floor((Date.now() - t0) / 1000));
    }, 1000);
    return () => window.clearInterval(id);
  }, [active]);
  return secs;
}

/** Format seconds for the ETA text — "~5с" / "~30с" / "~1.5м".
 *  We round to coarse buckets so the number stops jiggling
 *  every render; an ETA jiggling between 23 and 21 seconds
 *  feels worse than one stable value. */
function formatEta(seconds: number, lang: 'ru' | 'en'): string {
  const s = Math.max(1, Math.round(seconds));
  if (s < 60) {
    // Round to nearest 5s for the first minute.
    const rounded = Math.max(5, Math.round(s / 5) * 5);
    return lang === 'ru' ? `~${rounded}с` : `~${rounded}s`;
  }
  const m = s / 60;
  return lang === 'ru' ? `~${m.toFixed(1)}м` : `~${m.toFixed(1)}m`;
}

/** Loader chip with an inline progress fill + ETA.
 *  • If `fraction` is provided, a darker overlay grows left-to-right
 *    INSIDE the chip — visible on every background (heatmap blue
 *    included) because it stays inside the chip's solid backdrop.
 *  • ETA appears only after `elapsed >= ETA_SHOW_AFTER` (5 s by
 *    default) so most fast loads finish without ever flashing a
 *    countdown. Once shown, ETA never reverts to elapsed seconds —
 *    no UI swap mid-load.
 *  • `withEta=false` strips the right-side text entirely (used for
 *    the frame loader, where there's nothing meaningful to estimate
 *    and the chip should just be a label). */
const ETA_SHOW_AFTER_SECONDS = 5;

function LoaderChip({
  label, elapsed, fraction, eta, withEta = true, dim = 'normal',
}: {
  label: string;
  elapsed: number;
  /** 0..1 — when set, render the inline progress fill. */
  fraction?: number | null;
  /** Estimated remaining seconds. */
  eta?: number | null;
  /** Skip the right-side number entirely. Frame / overlay loaders
   *  that finish in < 1 s shouldn't flash an ETA at all. */
  withEta?: boolean;
  dim?: 'normal' | 'tile';
}) {
  const { lang } = useT();
  const showFill = typeof fraction === 'number' && fraction > 0;
  const showEta = withEta
    && elapsed >= ETA_SHOW_AFTER_SECONDS
    && typeof eta === 'number' && eta >= 1;
  const baseBg = dim === 'tile' ? 'bg-background/85' : 'bg-background/80';
  return (
    <div
      className={`relative overflow-hidden flex items-center gap-1.5 ${baseBg} border border-border rounded-md px-2 py-1 text-[11px] text-muted-foreground backdrop-blur-sm`}
    >
      {showFill && (
        <div
          className="absolute inset-y-0 left-0 bg-foreground/12 transition-all duration-500 ease-out"
          style={{ width: `${Math.max(0, Math.min(1, fraction)) * 100}%` }}
        />
      )}
      <Loader2 className="size-3 animate-spin relative z-10" />
      <span className="relative z-10">{label}</span>
      {showEta && (
        <span className="opacity-70 tabular-nums relative z-10">· {formatEta(eta!, lang)}</span>
      )}
    </div>
  );
}

/** Round seconds to "X h" / "X ч" for the warning-chip breakdown.
 *  One decimal under 10 h, integer otherwise — avoids "0.7 h" looking
 *  precise but reading rough. */
function formatHours(seconds: number, lang: 'ru' | 'en'): string {
  const h = seconds / 3600;
  const v = h < 10 ? h.toFixed(1) : Math.round(h).toString();
  return lang === 'ru' ? `${v} ч` : `${v} h`;
}

/** Place a tooltip near a (clientX, clientY) anchor while staying inside
 *  the viewport. Returns absolute-positioning props meant to be spread
 *  into a fixed-positioned element. We keep the tooltip horizontally
 *  centered on the anchor when there's room, and clamp `left` toward
 *  the viewport edge with an 8 px margin when there isn't. Vertically
 *  the tooltip prefers ABOVE the anchor; if the anchor is too close to
 *  the top of the viewport (< height + margin), it flips below.
 *
 *  `halfWidth` and `height` are estimates of the tooltip's box. They
 *  don't have to be exact — the clamp uses them to decide which side
 *  to bias toward, not to position pixel-perfectly. Pinned tooltips
 *  with the cell-tod chart need a much larger height than the live
 *  hover readout, so callers pass appropriate values. */
function clampTooltipPosition(
  clientX: number,
  clientY: number,
  halfWidth: number,
  height: number,
): { left: number; top: number; transform: string } {
  if (typeof window === 'undefined') {
    return { left: clientX, top: clientY - 14, transform: 'translate(-50%, -100%)' };
  }
  const margin = 8;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const left = Math.max(halfWidth + margin, Math.min(clientX, vw - halfWidth - margin));
  const showAbove = clientY > height + margin + 14;
  const top = showAbove
    ? Math.max(height + margin, clientY - 14)
    : Math.min(vh - margin, clientY + 14);
  const transform = showAbove ? 'translate(-50%, -100%)' : 'translate(-50%, 0)';
  return { left, top, transform };
}

/** Yellow warning chip — same outer shape as LoaderChip so the two
 *  read as a family when both appear stacked. Click toggles an
 *  expanded panel below the chip with the missing-data breakdown.
 *  The trigger stays visible in collapsed and expanded states so the
 *  user always has a one-click dismiss path. */
function WarningChip({
  coverage,
}: {
  coverage: {
    total_target_seconds: number;
    total_online_seconds: number;
    total_days: number;
    nonzero_days: number;
    kept_days: number;
    min_coverage_threshold: number;
  };
}) {
  const { t, lang } = useT();
  const [open, setOpen] = useState(false);
  const target = coverage.total_target_seconds;
  const online = coverage.total_online_seconds;
  const missing = Math.max(0, target - online);
  const missingPct = target > 0 ? Math.round(100 * missing / target) : 0;
  const recordedPct = target > 0 ? Math.round(100 * online / target) : 0;
  return (
    <div className="flex flex-col items-end gap-1.5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="relative overflow-hidden flex items-center gap-1.5 bg-amber-50 dark:bg-amber-900/40 border border-amber-300 dark:border-amber-600 rounded-md px-2 py-1 text-[11px] text-amber-900 dark:text-amber-100 backdrop-blur-sm hover:bg-amber-100 dark:hover:bg-amber-900/60 transition-colors shadow-sm"
      >
        <AlertTriangle className="size-3 relative z-10" />
        <span className="relative z-10">
          {t('heatmap.coverage.partialChip', { pct: missingPct })}
        </span>
      </button>
      {open && (
        <div className="bg-amber-50 dark:bg-amber-900/40 border border-amber-300 dark:border-amber-600 rounded-md px-3 py-2 text-[11px] text-amber-900 dark:text-amber-100 max-w-[18rem] shadow-md backdrop-blur-sm space-y-1">
          <div className="flex justify-between gap-3">
            <span className="opacity-80">{t('heatmap.coverage.detailRecorded')}</span>
            <span className="tabular-nums">
              {formatHours(online, lang)} / {formatHours(target, lang)} ({recordedPct}%)
            </span>
          </div>
          <div className="flex justify-between gap-3">
            <span className="opacity-80">{t('heatmap.coverage.detailMissing')}</span>
            <span className="tabular-nums">
              {formatHours(missing, lang)} ({missingPct}%)
            </span>
          </div>
          <div className="flex justify-between gap-3">
            <span className="opacity-80">{t('heatmap.coverage.detailFullDays')}</span>
            <span className="tabular-nums">
              {coverage.kept_days} / {coverage.total_days}
            </span>
          </div>
          <p className="pt-1 opacity-80 leading-snug">
            {t('heatmap.coverage.detailNote')}
          </p>
        </div>
      )}
    </div>
  );
}

/** Default per-(camera, day) cold-compute time in seconds. Calibrated
 *  against an observed 30-day × 7-camera load taking ~150 s on the
 *  current runserver, so ~150 / 30 ≈ 5 s per day per camera. Used
 *  as the starting estimate before any day-loop tick has landed,
 *  and blended with observed rate during the early days to keep
 *  the ETA from jumping wildly when the first day completes with
 *  an outlier duration. */
const HEATMAP_DEFAULT_PER_DAY_SECONDS = 5;

/** Compute per-subject ETA in seconds from a progress DTO + elapsed.
 *  Works for both heatmap and seating since they share the same
 *  per-day-loop progress shape (DailyProgressDto). Returns null
 *  when total_days isn't known yet (no progress payload at all —
 *  server hasn't written the initial counter).
 *
 *  Smoothing: ramp from "all default, no observation" at completed=0
 *  to "all observed, no default" at completed≥5. Removes the cliff
 *  where the very first observed day's duration would dominate the
 *  estimate. */
function computeHeatmapEta(
  progress: DailyProgressDto | null, elapsed: number,
): number | null {
  if (!progress || progress.total_days <= 0) return null;
  const remaining = progress.total_days - progress.completed_days;
  if (remaining <= 0) return 0;
  const observedRate = progress.completed_days > 0
    ? elapsed / progress.completed_days
    : HEATMAP_DEFAULT_PER_DAY_SECONDS;
  const w = Math.min(1, progress.completed_days / 5);
  const effectiveRate = w * observedRate
    + (1 - w) * HEATMAP_DEFAULT_PER_DAY_SECONDS;
  return remaining * effectiveRate;
}

/** Pick poll interval (ms) based on the current ETA so we don't
 *  spam the BE with a poll every 2 s when the user has 2 minutes
 *  left to wait. Tighter cadence as the wait shortens, so the bar
 *  feels live in the final seconds. */
function pollIntervalFromEta(etaSeconds: number | null): number {
  if (etaSeconds == null) return 3000;
  if (etaSeconds <= 30) return 3000;
  if (etaSeconds <= 120) return 5000;
  return 8000;
}

/** Poll a per-day-loop progress endpoint while `active` is true.
 *  Generic over heatmap and seating — caller passes a fetcher
 *  closure that hits the right endpoint with its own params. Both
 *  endpoints share the DailyProgressDto shape (see services/api/
 *  progress_cache.py).
 *
 *  Defensive against the BE being slow / hung:
 *    • Base interval scales with the current ETA (3 / 5 / 8 s) so
 *      parallel callers don't generate ~9 polls/sec against an
 *      already-loaded runserver. Tightens to 3 s under 30 s
 *      remaining so the final stretch feels live.
 *    • Exponential backoff to 8000 ms when consecutive polls return
 *      null. Resets on the first non-null reply.
 *
 *  TODO (OPS-4): a single WebSocket pushing per-subject progress
 *  would be strictly better than this fan-out polling — file when
 *  ASGI/Daphne is in place. */
function useDailyProgress(
  active: boolean,
  fetcher: () => Promise<DailyProgressDto | null>,
  deps: ReadonlyArray<unknown>,
): DailyProgressDto | null {
  const [progress, setProgress] = useState<DailyProgressDto | null>(null);
  useEffect(() => {
    if (!active) {
      setProgress(null);
      return;
    }
    let cancelled = false;
    let id: number | null = null;
    const startedAtMs = Date.now();
    let interval = 3000;
    const tick = async () => {
      const p = await fetcher();
      if (cancelled) return;
      if (p) {
        setProgress(p);
        // BE marks `done` either when completed_days reaches
        // total_days OR when the day loop crashed mid-flight (the
        // crash path sets done=true with completed < total so the
        // FE stops polling instead of waiting for the 5-min TTL).
        // Either way: stop scheduling new ticks.
        if (p.done) return;
        const elapsedSec = (Date.now() - startedAtMs) / 1000;
        const eta = computeHeatmapEta(p, elapsedSec);
        interval = pollIntervalFromEta(eta);
      } else {
        interval = Math.min(8000, Math.round(interval * 1.6));
      }
      id = window.setTimeout(tick, interval);
    };
    void tick();
    return () => {
      cancelled = true;
      if (id !== null) window.clearTimeout(id);
    };
  }, [active, ...deps]);
  return progress;
}

/** Heatmap-specific wrapper — thin alias around `useDailyProgress`
 *  so call sites read clearly. */
function useHeatmapProgress(
  active: boolean,
  cameraId: number,
  fromMs: number,
  toMs: number,
  mode: HeatmapMode,
  weekdays: SeatingWeekdays = 'all',
): DailyProgressDto | null {
  return useDailyProgress(
    active,
    () => getHeatmapProgress(cameraId, fromMs, toMs, mode, weekdays),
    [cameraId, fromMs, toMs, mode, weekdays],
  );
}

/** Seating-stats wrapper — same UX in the zones-mode loader chip. */
function useSeatingProgress(
  active: boolean,
  zoneId: string | number,
  from: string,
  to: string,
  weekdays: SeatingWeekdays = 'all',
): DailyProgressDto | null {
  return useDailyProgress(
    active,
    () => getSeatingProgress(zoneId, from, to, weekdays),
    [zoneId, from, to, weekdays],
  );
}

function formatHoursPerDay(
  absH: number,
  t: (k: string, p?: Record<string, string | number>) => string,
): string {
  if (absH < 1) {
    return t('heatmap.isoline.tooltipMinutes', { minutes: Math.round(absH * 60) });
  }
  return t('heatmap.isoline.tooltipHours', { hours: absH.toFixed(1) });
}

/** Bar chart for the click-tooltip's time-of-day breakdown.
 *  Two modes: 'dow' (7 bars Mon-Sun) and 'hour' (24 bars 0-23 local).
 *  • Bar HEIGHT is scaled to the per-chart max so the strongest bin
 *    reads at full height — keeps relative patterns visible even for
 *    sparse cells.
 *  • Bar COLOR uses the heatmap's Spectral_r scale referenced to the
 *    cell's overall density (globalPeak in h/day → globalPeak×60 min/day).
 *    A 20-min/day cell that's blue on the heatmap shows blue bars too,
 *    instead of "tallest bar = red" (which made low-density cells
 *    misleadingly look saturated).
 *  • mask[i]=false bins (non-working hours/days) are dropped entirely,
 *    so a Mon-Fri-only zone shows 5 bars in dow mode and a 09-22 zone
 *    shows 13 bars in hour mode — no zero-column dilution. */
function formatMinutesShort(m: number, lang: 'ru' | 'en'): string {
  if (m >= 60) {
    const hours = m / 60;
    return lang === 'ru' ? `${hours.toFixed(1)}ч` : `${hours.toFixed(1)}h`;
  }
  return lang === 'ru' ? `${Math.round(m)}м` : `${Math.round(m)}m`;
}

function CellTodChart({
  data, mode, lang, globalPeak,
}: {
  data: CellTimeOfDayDto;
  mode: 'dow' | 'hour';
  lang: 'ru' | 'en';
  /** Heatmap's max_density (h/day) — used to keep bar colors on the
   *  same absolute scale as the heatmap. Falls back to per-chart max
   *  when 0 (no data). */
  globalPeak: number;
}) {
  const rawValues = mode === 'dow' ? data.dow_minutes : data.hour_minutes;
  const rawMask = (mode === 'dow' ? data.dow_mask : data.hour_mask)
    ?? rawValues.map(() => true);
  // Drop bins with no working-hours coverage — closed weekdays / closed
  // hours of the day. If the BE didn't emit a mask (older payloads), we
  // fall back to "show every bin".
  const indices = rawValues.map((_, i) => i).filter((i) => rawMask[i]);
  if (indices.length === 0) {
    return null;
  }
  const values = indices.map((i) => rawValues[i]);
  const maxVal = Math.max(...values, 0.001);
  // Color reference: heatmap's overall peak in min/day. Bars colored
  // against this scale match the cell's heatmap color.
  const peakMinutesAbs = Math.max(globalPeak * 60.0, maxVal, 1);
  const barW = mode === 'dow' ? 22 : 10;
  const gap = mode === 'dow' ? 4 : 2;
  const chartH = 56;
  const labelH = 14;
  const yAxisW = 30;
  const totalBarsW = values.length * barW + Math.max(values.length - 1, 0) * gap;
  const totalW = yAxisW + totalBarsW;
  const dowLabels = lang === 'ru'
    ? ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']
    : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  // For hours, only label every 6 local hours so the strip stays readable.
  const hourLabel = (h: number) => (h % 6 === 0 ? String(h) : '');
  return (
    <svg width={totalW} height={chartH + labelH} style={{ display: 'block' }}>
      {/* Y-axis: top tick = the chart's max value (formatted),
          bottom tick = 0. Tells you what 'tallest bar' means in
          minutes — without it the chart has no magnitude reference. */}
      <line x1={yAxisW - 1} y1={0} x2={yAxisW - 1} y2={chartH}
        stroke="currentColor" strokeOpacity={0.2} strokeWidth={0.75} />
      <text x={yAxisW - 4} y={9} fontSize={9} textAnchor="end" fill="currentColor" opacity={0.7}>
        {formatMinutesShort(maxVal, lang)}
      </text>
      <text x={yAxisW - 4} y={chartH - 1} fontSize={9} textAnchor="end" fill="currentColor" opacity={0.5}>
        0
      </text>
      {values.map((v, i) => {
        const idx = indices[i];
        const barH = (v / maxVal) * chartH;
        const x = yAxisW + i * (barW + gap);
        const tColorNorm = Math.min(1, v / peakMinutesAbs);
        const tColor = 0.92 * Math.pow(tColorNorm, 0.5);
        const [rC, gC, bC] = spectralRColor(tColor);
        const fill = v > 0 ? `rgb(${rC},${gC},${bC})` : 'rgba(160,160,160,0.4)';
        const label = mode === 'dow' ? dowLabels[idx] : hourLabel(idx);
        return (
          <g key={idx}>
            <rect x={x} y={chartH - barH} width={barW} height={Math.max(barH, 0.5)} fill={fill} rx={1} />
            {label && (
              <text
                x={x + barW / 2}
                y={chartH + labelH - 3}
                fontSize={9}
                textAnchor="middle"
                fill="currentColor"
                opacity={0.65}
              >
                {label}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

/** Render a smooth Spectral_r gradient (data: URL) at grid resolution. The
 *  browser bilinearly upscales the small canvas to the camera viewBox, so a
 *  240×135 grid produces a smooth gradient at 1920×1080 with no banding.
 *
 *  The intensity used for color *and* alpha is `raw_density / globalPeak` so
 *  cameras self-calibrated peaks no longer disagree about what "red" means —
 *  same hours/day at this point produces the same color across all cameras
 *  in the panel. localPeak is needed because grid.values is normalized to
 *  the per-camera peak; multiplying recovers the raw hours/day. */
// Cells below this absolute floor render fully transparent. Avoids
// painting "5-10 min/day noise" as visible heatmap. Anything above this
// participates in the color/alpha curves below.
const HEATMAP_MIN_HOURS_PER_DAY = 20.0 / 60.0; // 20 min/day

/** Build the SVG path for the cutoff contour — the boundary curve where
 *  display density crosses HEATMAP_MIN_HOURS_PER_DAY. d3-contour does
 *  marching squares with linear interpolation between cell centers,
 *  giving short straight-line segments. We smooth each ring with
 *  quadratic Bézier curves through edge midpoints (each vertex becomes
 *  the control point of a Q-curve segment) so the rendered boundary
 *  looks like a smooth curve, not a polygonal stair-step.
 *  Used as a clip-path on the gradient image. */
function buildCutoffPath(
  values: number[],
  cols: number,
  rows: number,
  localPeak: number,
  natW: number,
  natH: number,
): string {
  if (!values.length || localPeak <= 0) return '';
  const tNorm = HEATMAP_MIN_HOURS_PER_DAY / localPeak;
  if (tNorm >= 1) return '';
  const generator = d3Contours().size([cols, rows]).thresholds([tNorm]);
  const features = generator(values);
  if (!features.length) return '';
  const scaleX = natW / cols;
  const scaleY = natH / rows;
  const mid = (a: [number, number], b: [number, number]): [number, number] =>
    [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  let d = '';
  for (const feature of features) {
    for (const polygon of feature.coordinates) {
      for (const ring of polygon) {
        if (ring.length < 3) continue;
        const pts = ring.map((pt) => [pt[0] * scaleX, pt[1] * scaleY] as [number, number]);
        const n = pts.length;
        const start = mid(pts[n - 1], pts[0]);
        d += `M${start[0].toFixed(1)},${start[1].toFixed(1)}`;
        for (let i = 0; i < n; i++) {
          const ctrl = pts[i];
          const next = mid(pts[i], pts[(i + 1) % n]);
          d += ` Q${ctrl[0].toFixed(1)},${ctrl[1].toFixed(1)} ${next[0].toFixed(1)},${next[1].toFixed(1)}`;
        }
        d += ' Z ';
      }
    }
  }
  return d.trim();
}

function buildGradientDataUrl(
  values: number[],
  cols: number,
  rows: number,
  localPeak: number,
  globalPeak: number,
): string | null {
  if (!values.length || globalPeak <= 0) return null;
  const canvas = document.createElement('canvas');
  canvas.width = cols;
  canvas.height = rows;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  const img = ctx.createImageData(cols, rows);
  // Map raw hours/day into a normalized [0..1] above the absolute cutoff:
  //   t_norm = clamp((raw - cutoff) / (peak - cutoff), 0, 1)
  // Then apply gamma 0.5 so the bottom of the visible range gets MORE
  // color spread than the top — fixing "12 min looks ≈ 2 h" where linear
  // mapping compressed the meaningful low band into a narrow green slice
  // while the top band (3-5 h) ate most of the palette.
  // NB: cells below cutoff are NOT zeroed out — they get the base alpha
  // (deepest blue, low intensity). The clipPath at the cutoff contour
  // cuts them off at render time, and keeping their canvas value
  // populated avoids a 0↔0.45 bilinear fade at the clip boundary that
  // would re-introduce the ragged-edge artifact.
  const range = Math.max(1e-6, globalPeak - HEATMAP_MIN_HOURS_PER_DAY);
  for (let i = 0; i < values.length; i++) {
    const raw = values[i] * localPeak;
    const tNorm = Math.max(0, Math.min(1, (raw - HEATMAP_MIN_HOURS_PER_DAY) / range));
    const tEased = Math.pow(tNorm, 0.5);
    // Anchor the bottom of the visible range at palette t=0 (violet,
    // #5e4fa2) — the literal bottom of the spectral_r colormap. The
    // colorbar's bottom label reads "20 min/day" so the user expects
    // "just at cutoff" to look like the very bottom of the legend.
    // Top still pulled in slightly (0.92, just below the deepest red
    // #9e0142) so peak cells don't render as the near-black extreme.
    const tColor = 0.92 * tEased;
    const [r, g, b] = spectralRColor(tColor);
    // Alpha curve: base + intensity-driven climb. Trimmed slightly
    // (was 0.45 → 0.85) so the heatmap doesn't smother the camera
    // image at peak cells — now 0.40 → 0.78, giving the underlying
    // frame a hair more visibility everywhere.
    const alpha = Math.round((0.40 + 0.38 * tEased) * 255);
    img.data[i * 4] = r;
    img.data[i * 4 + 1] = g;
    img.data[i * 4 + 2] = b;
    img.data[i * 4 + 3] = alpha;
  }
  ctx.putImageData(img, 0, 0);
  return canvas.toDataURL('image/png');
}

/** Even-odd point-in-polygon test for a single ring (array of [x, y]). */
function pointInRing(x: number, y: number, ring: number[][]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    const intersects = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

interface RenderableHotZone {
  d: string;
  data: HotZoneDto;
}

/** Render server-supplied hot zones into SVG paths. Polygons are smoothed
 *  via Catmull-Rom-like quadratic interpolation between hull vertices so
 *  the convex-hull edges read as soft blobs rather than angular polygons. */
function renderHotZones(zones: HotZoneDto[], natW: number, natH: number): RenderableHotZone[] {
  return zones.map((zone) => {
    const pts = zone.polygon.map(([nx, ny]) => [nx * natW, ny * natH] as [number, number]);
    if (pts.length < 3) {
      const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ') + ' Z';
      return { d, data: zone };
    }
    // Smooth with quadratic curves through midpoints. Each segment uses the
    // current vertex as control point, with start/end at the midpoint of the
    // adjacent edges. Closes the loop seamlessly.
    const mid = (a: [number, number], b: [number, number]): [number, number] =>
      [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
    const n = pts.length;
    const start = mid(pts[n - 1], pts[0]);
    let d = `M${start[0].toFixed(1)},${start[1].toFixed(1)}`;
    for (let i = 0; i < n; i++) {
      const ctrl = pts[i];
      const next = mid(pts[i], pts[(i + 1) % n]);
      d += ` Q${ctrl[0].toFixed(1)},${ctrl[1].toFixed(1)} ${next[0].toFixed(1)},${next[1].toFixed(1)}`;
    }
    d += ' Z';
    return { d, data: zone };
  });
}

/** Map a cluster's occupied_hours_per_day to a Spectral_r color, on the
 *  same hours-per-day-equivalent scale the heatmap itself uses. Cluster
 *  with 9 h/day occupancy reads as the same warm color a heatmap cell
 *  at 9 h/day would. Normalized against globalPeak (= the colorbar's
 *  hot end across cameras), gamma 0.5 to spread the low band so 1-2 h/
 *  day clusters don't all collapse into the same blue. */
function hotZoneFill(occupiedHours: number, globalPeak: number, hovered: boolean): string {
  const peakRef = Math.max(1e-6, globalPeak);
  const t = Math.min(1, occupiedHours / peakRef);
  const tColor = 0.10 + 0.82 * Math.pow(t, 0.5);
  const [r, g, b] = spectralRColor(tColor);
  const alpha = hovered ? 0.80 : 0.62;
  return `rgba(${r},${g},${b},${alpha.toFixed(2)})`;
}

interface LayerSwitcherProps {
  mode: LayerMode;
  onChange: (mode: LayerMode) => void;
  /** Modes to render. Defaults to all. Used to hide superuser-only modes
   *  ('hotzones') from regular users while keeping the BE radius mode
   *  reachable for supers. */
  availableModes?: LayerMode[];
}

/** Generic segmented control + Select pair. Period and layer pickers
 *  live next to each other in compare-pane headers, so they share one
 *  component to keep their style locked: same border, same shadow,
 *  same active/inactive treatment.
 *
 *  Two visual variants: a segmented bar on sm+ screens and the
 *  project's styled `<Select>` on smaller screens — the bar would
 *  wrap or steal width from its neighbour (especially in compare
 *  mode where two panes share one row on mobile). The Select gives
 *  us the same chevron + padding as every other dropdown in the FE
 *  (vs the native select that renders the OS chevron flush against
 *  the border). */
function SegmentedControl<T extends string>({
  value, onChange, options, ariaLabel,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
  ariaLabel?: string;
}) {
  return (
    <>
      <div className="sm:hidden inline-block w-auto max-w-[12rem]">
        <Select
          value={value}
          onChange={(e) => onChange(e.target.value as T)}
          aria-label={ariaLabel}
        >
          {options.map((opt) => (
            <option key={String(opt.value)} value={String(opt.value)}>{opt.label}</option>
          ))}
        </Select>
      </div>
      <div
        role="tablist"
        className="hidden sm:inline-flex rounded-lg border border-border bg-background overflow-hidden text-sm shadow-sm"
      >
        {options.map((opt, i) => (
          <button
            key={String(opt.value)}
            role="tab"
            type="button"
            onClick={() => onChange(opt.value)}
            aria-selected={value === opt.value}
            className={[
              'px-3 py-1.5 transition-colors',
              i > 0 ? 'border-l border-border' : '',
              value === opt.value
                ? 'bg-primary text-primary-foreground'
                : 'hover:bg-muted text-muted-foreground',
            ].join(' ')}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </>
  );
}

/** Period selector — thin wrapper around SegmentedControl. */
function PeriodPicker({
  value, onChange, t,
}: {
  value: Period;
  onChange: (p: Period) => void;
  t: (k: string, p?: Record<string, string | number>) => string;
}) {
  return (
    <SegmentedControl<Period>
      value={value}
      onChange={onChange}
      options={ALL_PERIODS.map((p) => ({ value: p, label: t(`heatmap.period.${p}`) }))}
      ariaLabel={t('heatmap.periodLabel')}
    />
  );
}

/** Layer mode selector — thin wrapper around SegmentedControl. */
function LayerSwitcher({ mode, onChange, availableModes = ALL_LAYER_MODES }: LayerSwitcherProps) {
  const { t } = useT();
  return (
    <SegmentedControl<LayerMode>
      value={mode}
      onChange={onChange}
      options={availableModes.map((m) => ({ value: m, label: t(`heatmap.layer.${m}`) }))}
      ariaLabel={t('heatmap.layerLabel')}
    />
  );
}

// Colorbar — passive vertical Spectral_r legend, bottom-right of camera tile.
// Top label = peak h/day (the value the FE renders red at); bottom label =
// the cutoff (= 20 min/day) where the palette starts at its lowest stop
// (violet). Anything below the cutoff renders transparent.
function Colorbar({ maxDensity }: { maxDensity: number }) {
  const { t } = useT();
  const gradientStops = SPECTRAL_R_STOPS
    .map(([stop, [r, g, b]]) => `rgb(${r},${g},${b}) ${stop * 100}%`)
    .join(', ');
  const cutoffMinutes = Math.round(HEATMAP_MIN_HOURS_PER_DAY * 60);

  return (
    <div
      style={{
        position: 'absolute',
        bottom: '8px',
        right: '8px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '3px',
        pointerEvents: 'none',
      }}
    >
      <span style={{ fontSize: '9px', lineHeight: 1, color: 'rgba(255,255,255,0.9)', textShadow: '0 1px 3px rgba(0,0,0,0.7)', textAlign: 'center' }}>
        {maxDensity.toFixed(1)}
        <br />
        {t('heatmap.colorbar.peak')}
      </span>
      <div
        style={{
          width: '10px',
          height: '64px',
          background: `linear-gradient(to top, ${gradientStops})`,
          borderRadius: '3px',
          border: '1px solid rgba(255,255,255,0.4)',
          position: 'relative',
        }}
      >
        {[0.2, 0.4, 0.6, 0.8].map((lvl) => (
          <div
            key={lvl}
            style={{
              position: 'absolute',
              left: '100%',
              top: `${(1 - lvl) * 100}%`,
              transform: 'translateY(-50%)',
              width: '4px',
              height: '1px',
              background: 'rgba(255,255,255,0.6)',
              marginLeft: '1px',
            }}
          />
        ))}
      </div>
      <span style={{ fontSize: '9px', lineHeight: 1, color: 'rgba(255,255,255,0.9)', textShadow: '0 1px 3px rgba(0,0,0,0.7)', textAlign: 'center' }}>
        {t('heatmap.colorbar.cutoffMinutes', { minutes: cutoffMinutes })}
      </span>
    </div>
  );
}

function parseWorkingHours(
  rules: ZoneWorkingHoursDto[],
  lang: 'ru' | 'en',
): { label: string; avgHoursPerDay: number | null } {
  const weekly = rules.filter((r) => r.date === null && r.open_time && r.close_time);
  if (!weekly.length) return { label: '', avgHoursPerDay: null };

  function durationH(open: string, close: string): number {
    const [oh, om] = open.split(':').map(Number);
    const [ch, cm] = close.split(':').map(Number);
    return (ch * 60 + cm - (oh * 60 + om)) / 60;
  }

  type Group = { open: string; close: string; mask: number; hours: number };
  const groups: Group[] = [];
  for (const rule of weekly) {
    const existing = groups.find((g) => g.open === rule.open_time && g.close === rule.close_time);
    if (existing) {
      existing.mask |= rule.weekdays_mask;
    } else {
      groups.push({ open: rule.open_time!, close: rule.close_time!, mask: rule.weekdays_mask, hours: durationH(rule.open_time!, rule.close_time!) });
    }
  }

  function fmtTime(t: string) { return t.slice(0, 5); }

  function maskLabel(mask: number): string {
    const dayLabels = getWeekdayLabels(lang);
    const bits: number[] = [];
    for (let i = 0; i < 7; i++) if (mask & (1 << i)) bits.push(i);
    if (!bits.length) return '';
    if (bits.length === 7) return lang === 'ru' ? 'Ежедневно' : 'Daily';
    const contiguous = bits.every((b, i) => i === 0 || b === bits[i - 1] + 1);
    if (contiguous && bits.length > 1) return `${dayLabels[bits[0]]}–${dayLabels[bits[bits.length - 1]]}`;
    return bits.map((b) => dayLabels[b]).join(', ');
  }

  const dayHours: (number | null)[] = Array(7).fill(null);
  for (const g of groups) {
    for (let i = 0; i < 7; i++) {
      if (g.mask & (1 << i)) dayHours[i] = g.hours;
    }
  }
  const active = dayHours.filter((h) => h !== null) as number[];
  const avgHoursPerDay = active.length ? active.reduce((s, h) => s + h, 0) / active.length : null;

  let label: string;
  if (groups.length === 1) {
    const g = groups[0];
    const hoursLabel = lang === 'ru' ? `~${Math.round(g.hours)} ч/день` : `~${Math.round(g.hours)} h/day`;
    label = `${fmtTime(g.open)}–${fmtTime(g.close)} (${hoursLabel})`;
  } else {
    label = groups.map((g) => `${maskLabel(g.mask)} ${fmtTime(g.open)}–${fmtTime(g.close)}`).join(' · ');
  }

  return { label, avgHoursPerDay };
}

function topZoneInsight(polygons: SeatingPolygon[], t: (k: string, p?: Record<string, string | number>) => string): string | null {
  if (polygons.length < 2) return null;
  const ranked = [...polygons].sort((a, b) => b.occupied_hours_per_day - a.occupied_hours_per_day);
  const total = ranked.reduce((s, p) => s + p.occupied_hours_per_day, 0);
  if (total === 0) return null;
  const avg = total / ranked.length;
  const top = ranked[0];
  if (avg <= 0) return null;
  const ratio = top.occupied_hours_per_day / avg;
  if (ratio < 1.2) return null;
  const name = top.polygon_name ?? t('heatmap.unnamed.zone', { id: top.polygon_id });
  return t('heatmap.insight.topZone', { name, ratio: ratio.toFixed(1) });
}

/** 24×7 time-of-day matrix rendered as a CSS grid */
function TODGrid({ matrix }: { matrix: number[][] }) {
  const { t } = useT();
  const [hoveredCell, setHoveredCell] = useState<{ day: number; hour: number; val: number; rect: DOMRect } | null>(null);
  const maxVal = Math.max(...matrix.flat(), 0.01);
  const days = [
    t('heatmap.tod.mon'), t('heatmap.tod.tue'), t('heatmap.tod.wed'),
    t('heatmap.tod.thu'), t('heatmap.tod.fri'), t('heatmap.tod.sat'), t('heatmap.tod.sun'),
  ];
  return (
    <div className="overflow-x-auto">
      {hoveredCell && (() => {
        const pos = clampTooltipPosition(
          hoveredCell.rect.left + hoveredCell.rect.width / 2,
          hoveredCell.rect.top,
          110,
          36,
        );
        return (
        <div
          style={{
            position: 'fixed',
            left: pos.left,
            top: pos.top,
            transform: pos.transform,
            pointerEvents: 'none',
            zIndex: 50,
          }}
          className="bg-background text-foreground border border-border rounded-md px-2 py-1 text-xs shadow-md whitespace-nowrap"
        >
          {t('heatmap.tod.cellTooltip', {
            day: days[hoveredCell.day],
            hour: hoveredCell.hour,
            val: hoveredCell.val.toFixed(1),
          })}
        </div>
        );
      })()}
      <div className="inline-grid" style={{ gridTemplateColumns: 'auto repeat(24, 1.5rem)' }}>
        <div />
        {Array.from({ length: 24 }, (_, h) => (
          <div key={h} className="text-center text-[10px] text-muted-foreground pb-1">
            {h % 4 === 0 ? String(h) : ''}
          </div>
        ))}
        {matrix.map((row, d) => (
          <React.Fragment key={d}>
            <div className="text-xs text-muted-foreground pr-2 flex items-center leading-none"
              style={{ minWidth: '2.5rem' }}>
              {days[d]}
            </div>
            {row.map((val, h) => {
              const intensity = maxVal > 0 ? val / maxVal : 0;
              return (
                <div
                  key={h}
                  className="w-6 h-6 rounded-sm border border-border/20"
                  style={{ backgroundColor: `rgba(59,130,246,${Math.min(intensity, 1).toFixed(2)})` }}
                  onMouseEnter={(e) => setHoveredCell({ day: d, hour: h, val, rect: e.currentTarget.getBoundingClientRect() })}
                  onMouseLeave={() => setHoveredCell(null)}
                />
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

interface PolygonOverlay {
  id: number;
  name: string | null;
  points: number[][];
  avg: number;
  avgWhenOccupied: number;
  hoursPerDay: number;
}

function CameraZoomModal({
  cameraId,
  imgUrl,
  cameraName,
  overlays,
  overlaysLoading = false,
  overlaysProgressFraction = null,
  overlaysEta = null,
  fromMs,
  toMs,
  densityData: initialDensityData,
  mode: modeProp = DEFAULT_LAYER_MODE,
  globalPeak,
  onClose,
  weekdays = 'all',
}: {
  cameraId: number;
  imgUrl: string;
  cameraName: string;
  overlays: PolygonOverlay[];
  overlaysLoading?: boolean;
  overlaysProgressFraction?: number | null;
  overlaysEta?: number | null;
  fromMs: number;
  toMs: number;
  weekdays?: SeatingWeekdays;
  /** Initial density data passed by the parent tile (already fetched
   *  there). The modal does its own SWR fetch keyed by the modal's
   *  current heatmap mode so it can switch to 'radius' for free-draw /
   *  hotzones; SWR dedupes when the key matches the tile's fetch. */
  densityData?: DensityGridDto;
  mode?: LayerMode;
  globalPeak?: number;
  onClose: () => void;
}) {
  const { t, lang } = useT();
  const isAdmin = useCan('global.admin');
  const availableLayerModes = isAdmin ? ALL_LAYER_MODES : PUBLIC_LAYER_MODES;
  // Mode is owned locally so the modal can switch independently while open.
  // Initialised from the parent, but free to diverge until the modal closes.
  const [mode, setMode] = useState<LayerMode>(modeProp);
  // Same gate as the page-level: snap into the user's allowed set.
  useEffect(() => {
    if (!availableLayerModes.includes(mode)) {
      setMode(availableLayerModes[0]);
    }
  }, [availableLayerModes, mode]);
  const [imgDims, setImgDims] = useState<{ w: number; h: number } | null>(null);
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [mousePos, setMousePos] = useState<{ x: number; y: number } | null>(null);
  // Pin = click/tap. Survives mouseleave so mobile taps don't flicker.
  const [pinnedId, setPinnedId] = useState<number | null>(null);
  const [pinnedPos, setPinnedPos] = useState<{ x: number; y: number } | null>(null);
  const [hoveredHotZonePinned, setHoveredHotZonePinned] = useState<number | null>(null);
  const [hotZoneMousePinned, setHotZoneMousePinned] = useState<{ x: number; y: number } | null>(null);
  const [hoverPoint, setHoverPoint] = useState<
    { gx: number; gy: number; cx: number; cy: number; raw: number; clientX: number; clientY: number } | null
  >(null);
  // Pinned heatmap-cell hover (click survives mouseleave, like the polygon
  // and hot-zone pin states). Same pattern, same toggle behavior.
  const [pinnedHoverPoint, setPinnedHoverPoint] = useState<
    { gx: number; gy: number; cx: number; cy: number; raw: number; clientX: number; clientY: number } | null
  >(null);
  const [hoveredHotZone, setHoveredHotZone] = useState<number | null>(null);
  const [hotZoneMouse, setHotZoneMouse] = useState<{ x: number; y: number } | null>(null);
  // Touch input state. The native synthetic-mouse-events flow on touch
  // devices fires mousemove + click together on a tap, which used to
  // pin the charts AND show the hover tooltip on the same gesture. We
  // run touch through dedicated pointer handlers and suppress the
  // synthetic click by checking lastPointerTypeRef.
  //
  //   tapStartRef     — set on pointerdown for pointerType==='touch';
  //                     position lets us classify pointerup as tap
  //                     (no significant movement) vs drag.
  //   lastPointerType — read in onClick to drop the synthetic click
  //                     that fires after a touch tap.
  const tapStartRef = useRef<{ x: number; y: number } | null>(null);
  const lastPointerTypeRef = useRef<string>('mouse');
  const clearAllPins = React.useCallback(() => {
    setPinnedId(null);
    setHoveredHotZonePinned(null);
    setPinnedHoverPoint(null);
  }, []);
  // Free-draw state — only available in density mode within the modal.
  // `points` are normalized [0..1] image coords. `closed=true` once the user
  // clicks "Готово" or hits Enter. While drawing, a rubber-band line follows
  // the cursor.
  const [drawingActive, setDrawingActive] = useState(false);
  const [drawPoints, setDrawPoints] = useState<[number, number][]>([]);
  const [drawHover, setDrawHover] = useState<[number, number] | null>(null);
  const [drawClosed, setDrawClosed] = useState(false);
  // Click-tooltip's bar-chart mode. 'dow' = 7 bars (Mon..Sun);
  // 'hour' = 24 bars (0..23 in zone-local time). Defaults to 'dow'.
  const [todChartMode, setTodChartMode] = useState<'dow' | 'hour'>('dow');
  const imgRef = useRef<HTMLImageElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const displayedWidth = useElementWidth(svgRef, imgDims?.w);

  // BE mode dispatch. Free-draw needs `frame_cells` and the activity-
  // zones layer needs `hot_zones` — both are radius-mode-only, both
  // are admin-only features. Fall back to ellipse for everything else.
  const heatmapMode: HeatmapMode = isAdmin && (mode === 'hotzones' || drawingActive || drawClosed)
    ? 'radius'
    : 'ellipse';
  const { data: fetchedDensityData, isLoading: densityLoading } = useSWR(
    ['density-grid', cameraId, fromMs, toMs, heatmapMode, weekdays],
    () => getCameraDensityGrid(cameraId, fromMs, toMs, heatmapMode, weekdays),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
      dedupingInterval: 5 * 60_000,
      // Initial paint uses the parent tile's already-fetched data;
      // SWR swaps to its own result once mode-specific fetch completes.
      fallbackData: heatmapMode === 'ellipse' ? initialDensityData : undefined,
    },
  );
  const densityData = fetchedDensityData ?? initialDensityData;

  // Cell time-of-day fetch for the pinned tooltip's bar chart.
  // Triggered only when a cell is pinned (= click) and the cell value
  // is above the visible-cutoff (no chart for transparent cells). The
  // BE caches per (camera, cell, window, weekdays) for 24 h, so
  // re-clicking the same cell is instant.
  const todPinned = pinnedHoverPoint && pinnedHoverPoint.raw >= HEATMAP_MIN_HOURS_PER_DAY
    ? pinnedHoverPoint : null;
  const { data: cellTodData, isLoading: cellTodLoading } = useSWR(
    todPinned && mode === 'heatmap'
      ? ['cell-tod', cameraId, todPinned.gx, todPinned.gy, fromMs, toMs, weekdays]
      : null,
    () => getCellTimeOfDay(cameraId, todPinned!.gx, todPinned!.gy, fromMs, toMs, weekdays),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
      dedupingInterval: 5 * 60_000,
    },
  );

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        if (drawingActive || drawClosed) {
          setDrawingActive(false);
          setDrawPoints([]);
          setDrawHover(null);
          setDrawClosed(false);
        } else {
          onClose();
        }
      } else if (e.key === 'Enter' && drawingActive && drawPoints.length >= 3) {
        setDrawClosed(true);
        setDrawingActive(false);
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, drawingActive, drawClosed, drawPoints.length]);

  const peakForColor = globalPeak ?? densityData?.max_density ?? 0;

  const gradientUrl = useMemo(() => {
    if (mode !== 'heatmap' || !densityData?.grid.values.length) return null;
    return buildGradientDataUrl(
      densityData.grid.values,
      densityData.grid.cols,
      densityData.grid.rows,
      densityData.max_density,
      peakForColor,
    );
  }, [mode, densityData, peakForColor]);

  const hotZones = useMemo(() => {
    if (mode !== 'hotzones' || !densityData?.hot_zones?.length) return [];
    return renderHotZones(densityData.hot_zones, densityData.width, densityData.height);
  }, [mode, densityData]);
  const cutoffPath = useMemo(() => {
    if (!densityData?.grid?.values?.length) return '';
    return buildCutoffPath(
      densityData.grid.values,
      densityData.grid.cols,
      densityData.grid.rows,
      densityData.max_density,
      densityData.width,
      densityData.height,
    );
  }, [densityData]);

  // Modal loader timers — same elapsed-seconds UX as the tile, hooks
  // at component top level so React hook-order rules stay clean.
  const showOverlaysLoaderModal = mode === 'zones' && overlaysLoading;
  const showHeatmapLoaderModal = (mode === 'heatmap' || mode === 'hotzones') && densityLoading;
  const elapsedOverlaysModal = useElapsedSeconds(showOverlaysLoaderModal);
  const elapsedHeatmapModal = useElapsedSeconds(showHeatmapLoaderModal);
  const heatmapProgressModal = useHeatmapProgress(
    showHeatmapLoaderModal, cameraId, fromMs, toMs, heatmapMode, weekdays,
  );
  const heatmapProgressFractionModal = heatmapProgressModal && heatmapProgressModal.total_days > 0
    ? heatmapProgressModal.completed_days / heatmapProgressModal.total_days
    : 0;

  // Free-draw stats — EXACT, computed from the per-frame detection cells
  // shipped by the backend. Same shape as the seating endpoint's
  // annotated-zone stats:
  //   occupiedHoursPerDay = (Σ gap for frames with ≥1 detection in poly)
  //                         × 24 / total_online_seconds
  //   avgWhenOccupied     = Σ (count_in_poly × gap) / occupied_seconds
  //                         (gap-weighted mean concurrent count when ≥1)
  // No more sum-pool approximation — we read the actual per-frame
  // detection list and count exactly.
  const drawIntegral = useMemo(() => {
    if (!drawClosed || drawPoints.length < 3) return null;
    if (!densityData?.frame_cells || !densityData.days_in_window) {
      return { occupiedHoursPerDay: 0, avgWhenOccupied: 1, totalHoursPerDay: 0 };
    }
    const cols = densityData.grid.cols;
    const rows = densityData.grid.rows;
    const days = densityData.days_in_window;
    // Build a fast in-polygon lookup mask via scan-line.
    const inPoly = new Uint8Array(rows * cols);
    const ringCell = drawPoints.map(([nx, ny]) => [nx * cols, ny * rows] as [number, number]);
    const np = ringCell.length;
    let minY = Infinity, maxY = -Infinity;
    for (const [, y] of ringCell) { if (y < minY) minY = y; if (y > maxY) maxY = y; }
    const r0 = Math.max(0, Math.floor(minY));
    const r1 = Math.min(rows, Math.ceil(maxY));
    for (let r = r0; r < r1; r++) {
      const y = r + 0.5;
      const xs: number[] = [];
      let j = np - 1;
      for (let i = 0; i < np; i++) {
        const [xi, yi] = ringCell[i];
        const [xj, yj] = ringCell[j];
        if ((yi > y) !== (yj > y)) {
          xs.push(((xj - xi) * (y - yi)) / (yj - yi) + xi);
        }
        j = i;
      }
      if (xs.length < 2) continue;
      xs.sort((a, b) => a - b);
      for (let k = 0; k + 1 < xs.length; k += 2) {
        const c0 = Math.max(0, Math.ceil(xs[k] - 0.5));
        const c1 = Math.min(cols - 1, Math.floor(xs[k + 1] - 0.5));
        const rowOff = r * cols;
        for (let c = c0; c <= c1; c++) inPoly[rowOff + c] = 1;
      }
    }
    // Iterate frames, count detections inside polygon per frame.
    let occupiedSeconds = 0;
    let weightedCount = 0;
    let peakSingle = 0;
    const displayValues = densityData.grid.values;
    const localPeak = densityData.max_density;
    for (const entry of densityData.frame_cells) {
      const gap = entry[1];
      let n = 0;
      for (let i = 2; i < entry.length; i += 2) {
        const c = entry[i];
        const r = entry[i + 1];
        if (c >= 0 && c < cols && r >= 0 && r < rows && inPoly[r * cols + c]) n++;
      }
      if (n > 0) {
        occupiedSeconds += gap;
        weightedCount += n * gap;
      }
    }
    // peak h/day for tooltip = max single-cell display value inside polygon.
    for (let r = 0; r < rows; r++) {
      const rowOff = r * cols;
      for (let c = 0; c < cols; c++) {
        if (inPoly[rowOff + c]) {
          const d = (displayValues[rowOff + c] ?? 0) * localPeak;
          if (d > peakSingle) peakSingle = d;
        }
      }
    }
    const occupiedHoursPerDay = days > 0 ? occupiedSeconds / days / 3600 : 0;
    const avgWhenOccupied = occupiedSeconds > 0 ? weightedCount / occupiedSeconds : 1;
    const totalHoursPerDay = days > 0 ? weightedCount / days / 3600 : 0;
    return {
      occupiedHoursPerDay,
      avgWhenOccupied,
      totalHoursPerDay,
      // peakSingleHoursPerDay also useful as a "busiest spot" indicator
      peakSingleHoursPerDay: peakSingle,
    };
  }, [drawClosed, drawPoints, densityData]);

  const displayedOverlayId = pinnedId ?? hoveredId;
  const displayedOverlay = overlays.find((o) => o.id === displayedOverlayId);
  const displayedMousePos = pinnedId !== null ? pinnedPos : mousePos;
  const displayedHotZoneIdx = hoveredHotZonePinned ?? hoveredHotZone;
  const displayedHotZoneMouse = hoveredHotZonePinned !== null ? hotZoneMousePinned : hotZoneMouse;
  // Pinned heatmap-cell tooltip > live hover. Used for the gradient-mode
  // hover-dot + tooltip rendering.
  const displayedHoverPoint = pinnedHoverPoint ?? hoverPoint;

  // Helper: convert a screen point to normalized image coords [0..1].
  function svgPointFromEvent(e: React.MouseEvent): [number, number] | null {
    const svg = svgRef.current;
    if (!svg || !imgDims) return null;
    const ctm = svg.getScreenCTM();
    if (!ctm) return null;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const local = pt.matrixTransform(ctm.inverse());
    return [Math.max(0, Math.min(1, local.x / imgDims.w)), Math.max(0, Math.min(1, local.y / imgDims.h))];
  }

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col items-center justify-between gap-3 bg-black/80 p-3 sm:p-4"
      onClick={onClose}
    >
      {/* Top bar — camera name + close button. Sits outside the image so
          mobile users always see them at consistent positions. */}
      <div
        className="w-full flex items-center justify-between gap-2"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-white text-sm font-medium truncate flex-1 min-w-0">{cameraName}</div>
        <button
          type="button"
          className="rounded-full bg-black/50 p-1.5 text-white hover:bg-black/70 shrink-0"
          onClick={onClose}
          aria-label="Close"
        >
          <X className="size-5" />
        </button>
      </div>
      {/* Image area — flex-1 so it fills the vertical space between header
          and controls. Click escapes propagation so clicks on the image
          don't dismiss the modal (the dark backdrop still does). */}
      <div
        className="relative flex items-center justify-center min-h-0 max-w-full"
        onClick={(e) => {
          // Click on empty image area = unpin all tooltips (same UX as the
          // tile card). Polygons / hot zones / heatmap rect stopPropagation,
          // so this only fires for clicks NOT on a pinable element.
          // stopPropagation prevents the backdrop's onClose from firing.
          e.stopPropagation();
          clearAllPins();
        }}
      >
        <div className="relative">
          <img
            ref={imgRef}
            src={imgUrl}
            alt={cameraName}
            className="max-w-[92vw] max-h-[72vh] sm:max-h-[78vh] object-contain block"
            onLoad={() => {
              if (imgRef.current) {
                setImgDims({ w: imgRef.current.naturalWidth, h: imgRef.current.naturalHeight });
              }
            }}
          />
          {/* Loader chips. Heatmap loader gains an inline progress
              fill + ETA when BE reports per-day cache hits/misses;
              the bar fills the chip itself (visible on every
              background, including the heatmap's blue cells), so we
              don't need a separate panel-wide overlay. */}
          {(() => {
            const anyLoading = showOverlaysLoaderModal || showHeatmapLoaderModal;
            if (!anyLoading) return null;
            // ETA from the first poll on, smoothed by blending observed
            // pace with a 5 s/day default until ≥5 days are sampled.
            const heatmapEta = computeHeatmapEta(heatmapProgressModal, elapsedHeatmapModal);
            return (
              <div className="absolute top-2 right-2 flex flex-col items-end gap-1 pointer-events-none">
                {showOverlaysLoaderModal && (
                  <LoaderChip
                    label={t('heatmap.camera.loadingOverlays')}
                    elapsed={elapsedOverlaysModal}
                    fraction={overlaysProgressFraction || null}
                    eta={overlaysEta}
                    dim="tile"
                  />
                )}
                {showHeatmapLoaderModal && (
                  <LoaderChip
                    label={t(mode === 'hotzones' ? 'heatmap.camera.loadingHotZones' : 'heatmap.camera.loadingHeatmap')}
                    elapsed={elapsedHeatmapModal}
                    fraction={heatmapProgressFractionModal || null}
                    eta={heatmapEta}
                    dim="tile"
                  />
                )}
              </div>
            );
          })()}
          {/* Partial-coverage warning — yellow chip in the top-right,
              same family as the loaders. Click to expand the
              missing-time breakdown. Renders the heatmap underneath
              regardless; only no_data falls back to a centered empty
              state. Property of the camera, so visible in every
              layer mode. */}
          {!densityLoading && densityData?.coverage
            && densityData.coverage.status === 'partial'
            && densityData.coverage.total_target_seconds > 0 && (
            <div className="absolute top-2 right-2 z-10 pointer-events-auto">
              <WarningChip coverage={densityData.coverage} />
            </div>
          )}
          {!densityLoading && densityData?.coverage
            && densityData.coverage.status === 'no_data' && (
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 max-w-[90%] text-center pointer-events-none">
              <div className="bg-background/90 border border-border rounded-md px-2 py-1 text-[10px] text-foreground shadow-sm backdrop-blur-sm">
                {t('heatmap.coverage.noDataTitle')}
              </div>
            </div>
          )}
          {imgDims && (
            <svg
              ref={svgRef}
              viewBox={`0 0 ${imgDims.w} ${imgDims.h}`}
              preserveAspectRatio="xMidYMid meet"
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
            >
              <defs>
                {/* Pure-visual blur on the gradient image — softens the
                    pixelated boundary the absolute hours/day cutoff would
                    otherwise leave at the edge of each visible region.
                    Doesn't touch the density data itself; only the
                    rendering. stdDeviation in image-pixel units (viewBox). */}
                {cutoffPath && (
                  <clipPath id="heatmap-cutoff-clip-modal">
                    <path d={cutoffPath} />
                  </clipPath>
                )}
                {/* Small blur applied INSIDE the clip — softens bilinear
                    cell-step artifacts in the interior. SVG paint order is
                    filter → clip, so the blurred edges still get cut
                    sharply at the contour. */}
                <filter id="heatmap-soft-tile-modal" x="-2%" y="-2%" width="104%" height="104%">
                  <feGaussianBlur stdDeviation={Math.max(0.8, imgDims.w * 0.0015)} />
                </filter>
              </defs>
              {/* Density gradient layer. The clip-path is the marching-
                  squares contour at the 20-min/day cutoff. Filter softens
                  cell-edge stairs; clip cuts sharply at the boundary. */}
              {mode === 'heatmap' && gradientUrl && densityData && (
                <>
                  <image
                    href={gradientUrl}
                    x={0}
                    y={0}
                    width={imgDims.w}
                    height={imgDims.h}
                    preserveAspectRatio="none"
                    filter="url(#heatmap-soft-tile-modal)"
                    clipPath={cutoffPath ? "url(#heatmap-cutoff-clip-modal)" : undefined}
                    style={{ imageRendering: 'auto' }}
                  />
                  <rect
                    x={0}
                    y={0}
                    width={imgDims.w}
                    height={imgDims.h}
                    fill="transparent"
                    style={{
                      pointerEvents: 'all',
                      // Over the heatmap area, always hide the system cursor —
                      // our custom dot replaces it whether or not there's data
                      // under the cursor (avoids the "cursor disappearing"
                      // flicker when crossing empty cells). In drawing mode
                      // the system crosshair is more useful (precise clicks),
                      // and once a polygon is closed there's nothing to do
                      // here so the default cursor returns.
                      cursor: drawingActive ? 'crosshair' : drawClosed ? 'default' : 'none',
                    }}
                    onPointerDown={(e) => {
                      lastPointerTypeRef.current = e.pointerType;
                      if (e.pointerType !== 'touch') return;
                      // Live readout begins on press and follows the
                      // finger via onPointerMove. Capture the start
                      // position so onPointerUp can classify
                      // tap-vs-drag.
                      e.currentTarget.setPointerCapture(e.pointerId);
                      tapStartRef.current = { x: e.clientX, y: e.clientY };
                      const norm = svgPointFromEvent(e);
                      if (!norm) return;
                      const { cols, rows, values } = densityData.grid;
                      const c = Math.max(0, Math.min(cols - 1, Math.floor(norm[0] * cols)));
                      const r = Math.max(0, Math.min(rows - 1, Math.floor(norm[1] * rows)));
                      const v = values[r * cols + c] ?? 0;
                      setHoverPoint({
                        gx: c, gy: r,
                        cx: norm[0] * imgDims.w, cy: norm[1] * imgDims.h,
                        raw: densityData.occupied_grid?.values?.[r * cols + c] ?? (v * densityData.max_density),
                        clientX: e.clientX, clientY: e.clientY,
                      });
                    }}
                    onPointerMove={(e) => {
                      const norm = svgPointFromEvent(e);
                      if (!norm) return;
                      const { cols, rows, values } = densityData.grid;
                      const c = Math.max(0, Math.min(cols - 1, Math.floor(norm[0] * cols)));
                      const r = Math.max(0, Math.min(rows - 1, Math.floor(norm[1] * rows)));
                      const v = values[r * cols + c] ?? 0;
                      if (drawingActive) {
                        setDrawHover([norm[0], norm[1]]);
                      }
                      // Always set hoverPoint so the cursor circle stays
                      // visible across the whole heatmap area; raw=0 means
                      // "over empty cell, no tooltip but show neutral dot".
                      setHoverPoint({
                        gx: c, gy: r,
                        cx: norm[0] * imgDims.w, cy: norm[1] * imgDims.h,
                        raw: densityData.occupied_grid?.values?.[r * cols + c] ?? (v * densityData.max_density),
                        clientX: e.clientX, clientY: e.clientY,
                      });
                    }}
                    onPointerLeave={(e) => {
                      // Only clear on mouse-leave. Touch never sends a
                      // leave we want to honour while still pressed —
                      // pointercancel handles abnormal release for us.
                      if (e.pointerType !== 'touch') {
                        setHoverPoint(null);
                        setDrawHover(null);
                      }
                    }}
                    onPointerCancel={() => {
                      tapStartRef.current = null;
                      setHoverPoint(null);
                      setDrawHover(null);
                    }}
                    onPointerUp={(e) => {
                      if (e.pointerType !== 'touch') return;
                      const start = tapStartRef.current;
                      tapStartRef.current = null;
                      // Hover state always clears on touch release; we
                      // either pinned (charts panel takes over) or the
                      // user lifted on a no-data cell (everything goes).
                      setHoverPoint(null);
                      setDrawHover(null);
                      if (!start) return;
                      const dx = e.clientX - start.x;
                      const dy = e.clientY - start.y;
                      const isTap = Math.hypot(dx, dy) < 10;
                      if (!isTap) return;
                      const norm = svgPointFromEvent(e);
                      if (!norm) {
                        setPinnedHoverPoint(null);
                        return;
                      }
                      const { cols, rows, values } = densityData.grid;
                      const c = Math.max(0, Math.min(cols - 1, Math.floor(norm[0] * cols)));
                      const r = Math.max(0, Math.min(rows - 1, Math.floor(norm[1] * rows)));
                      const v = values[r * cols + c] ?? 0;
                      const raw = densityData.occupied_grid?.values?.[r * cols + c] ?? (v * densityData.max_density);
                      if (raw < HEATMAP_MIN_HOURS_PER_DAY) {
                        // Tap on a no-data cell clears any pinned
                        // tooltip (matches the user's "tap should just
                        // close any tooltips" expectation).
                        setPinnedHoverPoint(null);
                        return;
                      }
                      const next = {
                        gx: c, gy: r,
                        cx: norm[0] * imgDims.w, cy: norm[1] * imgDims.h,
                        raw,
                        clientX: e.clientX, clientY: e.clientY,
                      };
                      setPinnedHoverPoint((prev) =>
                        prev && prev.gx === c && prev.gy === r ? null : next,
                      );
                    }}
                    onClick={(e) => {
                      // Suppress synthesized click after a touch tap;
                      // pin/unpin already handled in onPointerUp above.
                      if (lastPointerTypeRef.current === 'touch') return;
                      e.stopPropagation();
                      if (!drawingActive) {
                        // Same code path as tap on a polygon: pin tooltip on
                        // current cell; click same cell again to unpin.
                        if (!hoverPoint) return;
                        if (hoverPoint.raw < HEATMAP_MIN_HOURS_PER_DAY) {
                          setPinnedHoverPoint(null);
                          return;
                        }
                        setPinnedHoverPoint((prev) =>
                          prev && prev.gx === hoverPoint.gx && prev.gy === hoverPoint.gy
                            ? null
                            : hoverPoint,
                        );
                        return;
                      }
                      const norm = svgPointFromEvent(e);
                      if (!norm) return;
                      // Click near the first point (within 2% of width) closes the polygon.
                      if (drawPoints.length >= 3) {
                        const [fx, fy] = drawPoints[0];
                        const dx = norm[0] - fx;
                        const dy = norm[1] - fy;
                        if (Math.hypot(dx, dy) < 0.02) {
                          setDrawClosed(true);
                          setDrawingActive(false);
                          setDrawHover(null);
                          return;
                        }
                      }
                      setDrawPoints((prev) => [...prev, [norm[0], norm[1]]]);
                    }}
                  />
                </>
              )}
              {/* Hot-zone clusters */}
              {mode === 'hotzones' && hotZones.map((zone, i) => {
                const isActive = displayedHotZoneIdx === i;
                return (
                  <path
                    key={i}
                    d={zone.d}
                    fill={hotZoneFill(zone.data.occupied_hours_per_day, peakForColor, isActive)}
                    stroke="rgba(255,255,255,0.30)"
                    strokeWidth={isActive ? 1.2 : 0.8}
                    style={{ pointerEvents: 'all', cursor: 'pointer' }}
                    onMouseEnter={(e) => { setHoveredHotZone(i); setHotZoneMouse({ x: e.clientX, y: e.clientY }); }}
                    onMouseMove={(e) => setHotZoneMouse({ x: e.clientX, y: e.clientY })}
                    onMouseLeave={() => { setHoveredHotZone(null); setHotZoneMouse(null); }}
                    onClick={(e) => {
                      setHoveredHotZonePinned((prev) => (prev === i ? null : i));
                      setHotZoneMousePinned({ x: e.clientX, y: e.clientY });
                    }}
                  />
                );
              })}
              {/* Annotated zones */}
              {mode === 'zones' && overlays.map((overlay) => {
                if (!overlay.points.length) return null;
                const pts = overlay.points
                  .map(([x, y]) => `${x * imgDims.w},${y * imgDims.h}`)
                  .join(' ');
                const isActive = overlay.id === displayedOverlayId;
                return (
                  <polygon
                    key={overlay.id}
                    points={pts}
                    fill={isActive ? 'rgba(59,130,246,0.40)' : 'rgba(59,130,246,0.22)'}
                    stroke="#3b82f6"
                    strokeWidth={isActive ? 2.5 : 1.5}
                    style={{ pointerEvents: 'all', cursor: 'pointer' }}
                    onClick={(e) => {
                      setPinnedId((prev) => (prev === overlay.id ? null : overlay.id));
                      setPinnedPos({ x: e.clientX, y: e.clientY });
                    }}
                    onMouseEnter={(e) => { setHoveredId(overlay.id); setMousePos({ x: e.clientX, y: e.clientY }); }}
                    onMouseMove={(e) => setMousePos({ x: e.clientX, y: e.clientY })}
                    onMouseLeave={() => { setHoveredId(null); setMousePos(null); }}
                  />
                );
              })}
              {/* Hover-dot for heatmap mode. Small, fixed-size cursor with
                  a dark + white halo for contrast on any background, and
                  the hovered cell's palette color in the middle so the
                  cursor itself reads out the local intensity. The earlier
                  σ-scaled ring was deleted along with the radius-based
                  per-cell semantics — see the BE heatmap module's TODO
                  for 2026-08-06 if reviving. */}
              {mode === 'heatmap' && hoverPoint && densityData && !drawingActive && (() => {
                const hp = hoverPoint;
                const peakRef = peakForColor || densityData.max_density || 1;
                const overData = hp.raw >= HEATMAP_MIN_HOURS_PER_DAY;
                const range = Math.max(1e-6, peakRef - HEATMAP_MIN_HOURS_PER_DAY);
                const tNorm = overData ? Math.min(1, (hp.raw - HEATMAP_MIN_HOURS_PER_DAY) / range) : 0;
                const tColor = 0.10 + 0.82 * Math.pow(tNorm, 0.5);
                const [pr, pg, pb] = spectralRColor(tColor);
                // Cursor sizes are constant in SCREEN px (~7 px dot,
                // ~11 px dark halo, ~9 px white halo) — convert to
                // viewBox units via the SVG's rendered width so the
                // cursor stays the same size whether we're in tile
                // grid or fullscreen modal.
                const vbRatio = displayedWidth > 0 ? imgDims.w / displayedWidth : 1;
                const dotR = 7 * vbRatio;
                const darkHaloR = 11 * vbRatio;
                const whiteHaloR = 9 * vbRatio;
                return (
                  <g style={{ pointerEvents: 'none' }}>
                    <circle cx={hp.cx} cy={hp.cy} r={darkHaloR} fill="rgba(0,0,0,0.7)" />
                    <circle cx={hp.cx} cy={hp.cy} r={whiteHaloR} fill="rgba(255,255,255,0.95)" />
                    <circle
                      cx={hp.cx} cy={hp.cy} r={dotR}
                      fill={overData ? `rgb(${pr},${pg},${pb})` : 'rgba(140,140,140,0.95)'}
                    />
                  </g>
                );
              })()}
              {/* Free-draw polygon (in-progress and closed) */}
              {mode === 'heatmap' && drawPoints.length > 0 && (() => {
                const closed = drawClosed;
                const path = drawPoints
                  .map(([nx, ny], i) => `${i === 0 ? 'M' : 'L'}${nx * imgDims.w},${ny * imgDims.h}`)
                  .join(' ') + (closed ? ' Z' : '');
                const rubberPath = !closed && drawHover
                  ? `M${drawPoints[drawPoints.length - 1][0] * imgDims.w},${drawPoints[drawPoints.length - 1][1] * imgDims.h} L${drawHover[0] * imgDims.w},${drawHover[1] * imgDims.h}`
                  : '';
                return (
                  <g style={{ pointerEvents: 'none' }}>
                    {!closed && rubberPath && (
                      <path d={rubberPath} fill="none" stroke="rgba(255,255,255,0.6)" strokeDasharray="6 4" strokeWidth={imgDims.w * 0.0018} />
                    )}
                    <path
                      d={path}
                      fill={closed ? 'rgba(255,255,255,0.10)' : 'none'}
                      stroke="rgba(255,255,255,0.95)"
                      strokeWidth={imgDims.w * 0.002}
                      strokeLinejoin="round"
                    />
                    {drawPoints.map(([nx, ny], i) => (
                      <circle
                        key={i}
                        cx={nx * imgDims.w}
                        cy={ny * imgDims.h}
                        r={imgDims.w * 0.0035}
                        fill={i === 0 && !closed && drawPoints.length >= 3 ? 'rgba(34,197,94,0.95)' : 'rgba(255,255,255,0.95)'}
                        stroke="rgba(0,0,0,0.6)"
                        strokeWidth={imgDims.w * 0.0008}
                      />
                    ))}
                  </g>
                );
              })()}
            </svg>
          )}
          {/* Colorbar — must label whatever peak the painter is mapping
              colors against (peakForColor; cross-camera global peak when
              present, else local). Falling back to occupied_max used to
              put a different number on the fullscreen legend than the
              tile legend for the same camera. */}
          {densityData && mode !== 'zones' && (
            <Colorbar maxDensity={peakForColor || densityData.max_density} />
          )}
        </div>
      </div>
      {/* Bottom controls bar — outside the image area. Holds the layer
          switcher and (in heatmap mode) the draw button. On mobile this
          becomes the primary control surface; on desktop it sits below the
          image for consistency. */}
      <div
        className="w-full flex flex-wrap items-center justify-center gap-2"
        onClick={(e) => e.stopPropagation()}
      >
        <LayerSwitcher mode={mode} onChange={setMode} availableModes={availableLayerModes} />
        {mode === 'heatmap' && isAdmin && (
          <>
            {!drawingActive && !drawClosed && (
              <UiTooltip content={t('heatmap.freedraw.start')}><button
                type="button"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-border bg-background hover:bg-muted shadow-sm"
                onClick={() => { setDrawingActive(true); setDrawPoints([]); setDrawClosed(false); }}
                aria-label={t('heatmap.freedraw.start')}
              >
                <PenTool className="size-4" />
                <span className="hidden sm:inline">{t('heatmap.freedraw.startShort')}</span>
              </button></UiTooltip>
            )}
            {(drawingActive || drawClosed) && (
              <UiTooltip content={t('heatmap.freedraw.cancel')}><button
                type="button"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-border bg-background hover:bg-muted shadow-sm"
                onClick={() => { setDrawingActive(false); setDrawPoints([]); setDrawHover(null); setDrawClosed(false); }}
                aria-label={t('heatmap.freedraw.cancel')}
              >
                <Trash2 className="size-4" />
                <span className="hidden sm:inline">{t('heatmap.freedraw.cancel')}</span>
              </button></UiTooltip>
            )}
            {/* Live status: while drawing → hint; while closed → integral
                result formatted exactly like the hot-zone tooltip (hours/day
                + avg-when-present). The result replaces the hint so the user
                immediately sees the answer once they close the polygon. */}
            {drawingActive && !drawClosed && (
              <div className="text-xs text-white/80 px-2 py-1 max-w-[70vw] sm:max-w-[420px] truncate">
                {t('heatmap.freedraw.hint')}
              </div>
            )}
            {drawClosed && drawIntegral !== null && (
              <div className="bg-background/95 text-foreground border border-border rounded-lg px-3 py-1.5 text-xs shadow-md max-w-[70vw] sm:max-w-[420px]">
                <div className="font-medium">
                  {t('heatmap.barchart.hoursPerDay', { hours: drawIntegral.occupiedHoursPerDay.toFixed(1) })}
                </div>
                {drawIntegral.avgWhenOccupied > 0 && (
                  <div className="text-muted-foreground">
                    {t('heatmap.barchart.avgWhenOccupied', { avg: drawIntegral.avgWhenOccupied.toFixed(1) })}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
      {/* Polygon tooltip — pinned > hovered. Wraps long names. */}
      {displayedOverlay && displayedMousePos && (() => {
        const pos = clampTooltipPosition(displayedMousePos.x, displayedMousePos.y, 160, 90);
        return createPortal(
        <div
          style={{
            position: 'fixed',
            left: pos.left,
            top: pos.top,
            transform: pos.transform,
            pointerEvents: 'none',
            zIndex: 9999,
            maxWidth: 'min(85vw, 360px)',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
          }}
          className="bg-background/90 text-foreground border border-border rounded-lg px-3 py-1.5 text-sm shadow-md backdrop-blur-sm"
        >
          <div className="font-medium">
            {displayedOverlay.name ?? t('heatmap.unnamed.zone', { id: displayedOverlay.id })}
          </div>
          {(displayedOverlay.hoursPerDay > 0 || displayedOverlay.avgWhenOccupied > 0) && (
            <div className="text-muted-foreground text-xs">
              {displayedOverlay.hoursPerDay > 0
                ? t('heatmap.barchart.hoursPerDay', { hours: displayedOverlay.hoursPerDay.toFixed(1) })
                : ''}
              {displayedOverlay.hoursPerDay > 0 && displayedOverlay.avgWhenOccupied > 0 ? ' · ' : ''}
              {displayedOverlay.avgWhenOccupied > 0
                ? t('heatmap.barchart.avgWhenOccupied', { avg: displayedOverlay.avgWhenOccupied.toFixed(1) })
                : ''}
            </div>
          )}
        </div>,
        document.body,
      );
      })()}
      {/* Density tooltips — render BOTH pinned and live-hover separately
          when both exist on different cells, so clicking to pin the
          tooltip doesn't disable hover-readout afterwards. Hidden
          during drawing (crosshair cursor, no stat to read) and when
          below the cutoff. */}
      {mode === 'heatmap' && pinnedHoverPoint && pinnedHoverPoint.raw >= HEATMAP_MIN_HOURS_PER_DAY && !drawingActive && (() => {
        // Pinned tooltip carries the cell-tod chart, so it's the tallest
        // surface here — height estimate around ~190 px (chart + toggles
        // + heading). The clamp uses this to flip below when the cell
        // is too close to the viewport top.
        const pos = clampTooltipPosition(pinnedHoverPoint.clientX, pinnedHoverPoint.clientY, 190, 200);
        return createPortal(
        <div
          style={{
            position: 'fixed',
            left: pos.left,
            top: pos.top,
            transform: pos.transform,
            // Chart toggle needs pointer events; the rest of the
            // panel is decorative so we don't capture stray hovers.
            pointerEvents: 'auto',
            zIndex: 9999,
            maxWidth: 'min(90vw, 380px)',
          }}
          className="bg-background/95 text-foreground border border-primary/60 rounded-lg px-2.5 py-1.5 text-xs shadow-md backdrop-blur-sm"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="font-medium">{formatHoursPerDay(pinnedHoverPoint.raw, t)}</div>
          {/* Bar chart of when this point sees activity. Toggle picks
              between day-of-week and hour-of-day breakdowns. */}
          <div className="mt-1.5 flex items-center gap-1">
            {(['dow', 'hour'] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={(e) => { e.stopPropagation(); setTodChartMode(m); }}
                className={[
                  'px-2 py-0.5 rounded-md text-[10px] transition-colors',
                  todChartMode === m
                    ? 'bg-primary/85 text-primary-foreground'
                    : 'bg-muted/60 text-muted-foreground hover:bg-muted',
                ].join(' ')}
              >
                {t(m === 'dow' ? 'heatmap.cellTod.byDay' : 'heatmap.cellTod.byHour')}
              </button>
            ))}
          </div>
          <div className="mt-1 min-h-[72px] flex items-end">
            {cellTodLoading && !cellTodData && (
              <div className="text-muted-foreground text-[11px]">{t('common.loading')}</div>
            )}
            {cellTodData && (
              <CellTodChart
                data={cellTodData}
                mode={todChartMode}
                lang={lang}
                globalPeak={peakForColor || densityData?.max_density || 0}
              />
            )}
          </div>
        </div>,
        document.body,
      );
      })()}
      {mode === 'heatmap' && hoverPoint && hoverPoint.raw >= HEATMAP_MIN_HOURS_PER_DAY && !drawingActive
        && (!pinnedHoverPoint || pinnedHoverPoint.gx !== hoverPoint.gx || pinnedHoverPoint.gy !== hoverPoint.gy)
        && (() => {
        const pos = clampTooltipPosition(hoverPoint.clientX, hoverPoint.clientY, 100, 50);
        return createPortal(
        <div
          style={{
            position: 'fixed',
            left: pos.left,
            top: pos.top,
            transform: pos.transform,
            pointerEvents: 'none',
            zIndex: 9999,
            maxWidth: 'min(85vw, 360px)',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
          }}
          className="bg-background/90 text-foreground border border-border rounded-lg px-2.5 py-1 text-xs shadow-md backdrop-blur-sm"
        >
          {formatHoursPerDay(hoverPoint.raw, t)}
        </div>,
        document.body,
      );
      })()}
      {/* Hot-zone tooltip — pinned > hovered, same shape as annotated zone. */}
      {displayedHotZoneIdx !== null && displayedHotZoneIdx !== undefined && displayedHotZoneMouse && hotZones[displayedHotZoneIdx] && (() => {
        const pos = clampTooltipPosition(displayedHotZoneMouse.x, displayedHotZoneMouse.y, 160, 80);
        return createPortal(
        <div
          style={{
            position: 'fixed',
            left: pos.left,
            top: pos.top,
            transform: pos.transform,
            pointerEvents: 'none',
            zIndex: 9999,
            maxWidth: 'min(85vw, 360px)',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
          }}
          className="bg-background/90 text-foreground border border-border rounded-lg px-3 py-1.5 text-xs shadow-md backdrop-blur-sm"
        >
          <div className="font-medium">
            {t('heatmap.barchart.hoursPerDay', { hours: hotZones[displayedHotZoneIdx].data.occupied_hours_per_day.toFixed(1) })}
          </div>
          {hotZones[displayedHotZoneIdx].data.avg_when_occupied > 0 && (
            <div className="text-muted-foreground">
              {t('heatmap.barchart.avgWhenOccupied', { avg: hotZones[displayedHotZoneIdx].data.avg_when_occupied.toFixed(1) })}
            </div>
          )}
        </div>,
        document.body,
      );
      })()}
    </div>
  );
}

interface CameraTileProps {
  cameraId: number;
  cameraName: string;
  fromMs: number;
  toMs: number;
  /** Weekday filter for the BE per-day loop. Same value the parent
   *  ZonePanel passed to seating-stats — keeps the heatmap and the
   *  polygon table on the same set of days (8 weekend days / 10
   *  workdays). */
  weekdays?: SeatingWeekdays;
  overlays?: PolygonOverlay[];
  overlaysLoading?: boolean;
  /** Seating-stats per-day progress (computed at ZonePanel level
   *  since it's shared across every tile in the panel). 0..1 for
   *  the bar fill; eta for the chip's right-side text. */
  overlaysProgressFraction?: number | null;
  overlaysEta?: number | null;
  mode?: LayerMode;
  /** When set, color is calibrated against this peak (max max_density across
   *  the panel's cameras) so identical density yields identical color
   *  cross-camera. Falls back to the local max_density when undefined. */
  globalPeak?: number;
  /** Notify parent when this tile's density loads, so the parent can compute
   *  a panel-wide globalPeak. */
  onPeakLoaded?: (cameraId: number, peak: number) => void;
  /** Notify parent of coverage status (ok / partial / no_data) so the
   *  panel can demote bad-coverage tiles to the bottom of the grid. */
  onCoverageReported?: (cameraId: number, status: string | null) => void;
}

/**
 * CameraTile data flow
 * ====================
 * Two independent SWR fetches, both silent on failure. Neither one can put
 * the panel into an "error" state — failures just mean that piece of data
 * is absent and the corresponding visual element is skipped.
 *
 *   frame   = useSWR(['frame-latest', cameraId])    → fast (~50 ms, /frames/latest)
 *   density = useSWR(['density-grid', cam, fromMs, toMs]) → slow (~30 s cold)
 *
 * SWR config (both):
 *   shouldRetryOnError: false    — don't hammer a permanently broken endpoint
 *   revalidateOnFocus: false     — tab focus must not re-fetch
 *   revalidateOnReconnect: false — network blip must not refetch
 *   dedupingInterval: 5 min      — same key hit again uses cache
 *
 * IMPORTANT: do NOT wrap the fetcher in `.catch(() => null)`. That converts
 * a refetch failure into a successful response of `null`, which then
 * overwrites the previously-cached good data — and the frame/heatmap
 * disappears. Letting SWR own the error means stale-but-good data stays.
 *
 * Render: image and bands are independent. Image renders iff frame.data
 * exists and the image URL itself didn't 404. Bands render iff density.data
 * is loaded. Loading badge applies only to density. There is no full-panel
 * error UI — silence on failure.
 */
function CameraTile({
  cameraId,
  cameraName,
  fromMs,
  toMs,
  weekdays = 'all',
  overlays = [],
  overlaysLoading = false,
  overlaysProgressFraction = null,
  overlaysEta = null,
  mode = DEFAULT_LAYER_MODE,
  globalPeak,
  onPeakLoaded,
  onCoverageReported,
}: CameraTileProps) {
  const { t } = useT();
  const isAdmin = useCan('global.admin');
  // BE mode: 'radius' carries hot_zones / frame_cells (admin-only
  // 'hotzones' tab needs them); regular tiles use the simpler
  // 'ellipse' grid. Cache key includes the mode so both can coexist.
  const heatmapMode: HeatmapMode = isAdmin && mode === 'hotzones' ? 'radius' : 'ellipse';
  const [imgFailed, setImgFailed] = useState(false);
  const [imgDims, setImgDims] = useState<{ w: number; h: number } | null>(null);
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [mousePos, setMousePos] = useState<{ x: number; y: number } | null>(null);
  // Pinned (click/tap) overlay — survives mouseleave, so on touch devices
  // the tooltip doesn't disappear instantly after the synthetic mouseleave
  // that follows a tap. Click again on the same polygon (or anywhere else)
  // to unpin; tapping a different polygon switches the pin.
  const [pinnedId, setPinnedId] = useState<number | null>(null);
  const [pinnedPos, setPinnedPos] = useState<{ x: number; y: number } | null>(null);
  const [hoveredHotZonePinned, setHoveredHotZonePinned] = useState<number | null>(null);
  const [hotZoneMousePinned, setHotZoneMousePinned] = useState<{ x: number; y: number } | null>(null);
  const [zoomed, setZoomed] = useState(false);
  const [hoverPoint, setHoverPoint] = useState<
    { gx: number; gy: number; cx: number; cy: number; raw: number; clientX: number; clientY: number } | null
  >(null);
  // Pinned heatmap-cell hover.
  const [pinnedHoverPoint, setPinnedHoverPoint] = useState<
    { gx: number; gy: number; cx: number; cy: number; raw: number; clientX: number; clientY: number } | null
  >(null);
  const [hoveredHotZone, setHoveredHotZone] = useState<number | null>(null);
  const [hotZoneMouse, setHotZoneMouse] = useState<{ x: number; y: number } | null>(null);
  // Touch input state: tile is "drag = live readout, release on data
  // = open modal, release on no-data = close any tooltip". See the
  // matching block in CameraZoomModal for the rationale.
  const tapStartRef = useRef<{ x: number; y: number } | null>(null);
  const lastPointerTypeRef = useRef<string>('mouse');
  const clearAllPins = React.useCallback(() => {
    setPinnedId(null);
    setPinnedPos(null);
    setHoveredHotZonePinned(null);
    setHotZoneMousePinned(null);
    setPinnedHoverPoint(null);
  }, []);
  const imgRef = useRef<HTMLImageElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const displayedWidth = useElementWidth(svgRef, imgDims?.w);

  const swrOpts = {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    dedupingInterval: 5 * 60_000,
  };
  // Frame fetch is light (~50 ms) but fails intermittently on prod
  // not because the frame is missing — every active camera has frames
  // in the DB (verified 2026-05-11) — but because the BE single
  // worker saturates with concurrent density-grid + seating computes
  // and /frames/latest/ either hits the 30 s axios timeout or gets
  // cancelled by the browser. Density-grid is intentionally no-retry
  // (heavy compute, very different cost). Frame retries 3× with
  // exponential backoff (2 s, 4 s, 8 s). Track a local "exhausted"
  // flag so the placeholder only fires after we've genuinely given
  // up, not between retries.
  const [frameRetriesExhausted, setFrameRetriesExhausted] = useState(false);
  const [frameRetryNonce, setFrameRetryNonce] = useState(0);
  const { data: frameData, isLoading: frameLoading, isValidating: frameValidating, error: frameError, mutate: refetchFrame } = useSWR(
    ['frame-latest', cameraId, frameRetryNonce],
    () => getLatestFrame(String(cameraId)).then((r) => r.data),
    {
      ...swrOpts,
      shouldRetryOnError: true,
      errorRetryCount: 3,
      errorRetryInterval: 2000,
      revalidateOnFocus: true,
      onSuccess: () => setFrameRetriesExhausted(false),
      onErrorRetry: (_err, _key, _config, revalidate, opts) => {
        const next = (opts.retryCount ?? 0) + 1;
        if (next > 3) {
          setFrameRetriesExhausted(true);
          return;
        }
        // Exponential: 2 s, 4 s, 8 s. Buys time for the BE worker to
        // free up after a burst of heavy density-grid computes.
        setTimeout(() => revalidate({ retryCount: next }), 2000 * 2 ** (next - 1));
      },
    },
  );
  const { data: densityData, isLoading: densityLoading } = useSWR(
    ['density-grid', cameraId, fromMs, toMs, heatmapMode, weekdays],
    () => getCameraDensityGrid(cameraId, fromMs, toMs, heatmapMode, weekdays),
    swrOpts,
  );

  // Bubble peak up to parent for cross-camera color normalization.
  useEffect(() => {
    if (densityData && onPeakLoaded) {
      onPeakLoaded(cameraId, densityData.max_density);
    }
  }, [densityData, cameraId, onPeakLoaded]);

  // Bubble coverage status up so the panel can demote bad-coverage tiles
  // to the bottom of the grid (cleaner reading order).
  useEffect(() => {
    if (densityData && onCoverageReported) {
      onCoverageReported(cameraId, densityData.coverage?.status ?? null);
    }
  }, [densityData, cameraId, onCoverageReported]);

  // Image dimensions: prefer the loaded image's natural size (img.onLoad);
  // density grid width/height (= camera.max_x/y_resolution) is the fallback
  // so bands have an aspect ratio even before the image arrives or when the
  // camera has no image at all.
  useEffect(() => {
    if (densityData && !imgDims) {
      setImgDims({ w: densityData.width, h: densityData.height });
    }
  }, [densityData, imgDims]);

  const peakForColor = globalPeak ?? densityData?.max_density ?? 0;

  const gradientUrl = useMemo(() => {
    if (mode !== 'heatmap' || !densityData?.grid.values.length) return null;
    return buildGradientDataUrl(
      densityData.grid.values,
      densityData.grid.cols,
      densityData.grid.rows,
      densityData.max_density,
      peakForColor,
    );
  }, [mode, densityData, peakForColor]);

  const hotZones = useMemo(() => {
    if (mode !== 'hotzones' || !densityData?.hot_zones?.length) return [];
    return renderHotZones(densityData.hot_zones, densityData.width, densityData.height);
  }, [mode, densityData]);
  const cutoffPath = useMemo(() => {
    if (!densityData?.grid?.values?.length) return '';
    return buildCutoffPath(
      densityData.grid.values,
      densityData.grid.cols,
      densityData.grid.rows,
      densityData.max_density,
      densityData.width,
      densityData.height,
    );
  }, [densityData]);

  const imgUrl = !imgFailed && frameData?.image
    ? toAbsoluteUrl(frameData.image, API_HOST || undefined)
    : null;
  // Genuine failure = retries exhausted OR the <img> element errored
  // after a successful fetch. We don't want to surface a placeholder
  // between retries (3 s waits would cause flicker between "loading"
  // and "failed" states), so keep the loader chip visible the entire
  // time we're still trying.
  const frameFetchFailed = frameRetriesExhausted || imgFailed;
  const frameStillTrying = frameLoading || frameValidating || (Boolean(frameError) && !frameRetriesExhausted);
  // Loader elapsed-seconds (for the modal). Hooks must run at the
  // component top-level, not inside the loader IIFE below.
  const showFrameLoader = !imgUrl && !frameFetchFailed && frameStillTrying;
  const retryFrame = React.useCallback(() => {
    setImgFailed(false);
    setFrameRetriesExhausted(false);
    setFrameRetryNonce((n) => n + 1);
    void refetchFrame();
  }, [refetchFrame]);
  const showOverlaysLoader = mode === 'zones' && overlaysLoading;
  const showHeatmapLoader = (mode === 'heatmap' || mode === 'hotzones') && densityLoading;
  const elapsedFrame = useElapsedSeconds(showFrameLoader);
  const elapsedOverlays = useElapsedSeconds(showOverlaysLoader);
  const elapsedHeatmap = useElapsedSeconds(showHeatmapLoader);
  // Real progress driven by per-day cache hits/misses. Polling keyed
  // on the same params the main fetch uses so cache hits there → BE
  // never opens the per-day loop → progress endpoint returns null and
  // we fall back to the dim backdrop. Cold paths get a real fill.
  const heatmapProgress = useHeatmapProgress(
    showHeatmapLoader, cameraId, fromMs, toMs, heatmapMode, weekdays,
  );
  const heatmapProgressFraction = heatmapProgress && heatmapProgress.total_days > 0
    ? heatmapProgress.completed_days / heatmapProgress.total_days
    : 0;
  // Display tooltip = pinned > hovered. Mobile tap pins; tap-elsewhere unpins.
  const displayedOverlayId = pinnedId ?? hoveredId;
  const displayedOverlay = overlays.find((o) => o.id === displayedOverlayId);
  const displayedMousePos = pinnedId !== null ? pinnedPos : mousePos;
  const displayedHotZoneIdx = hoveredHotZonePinned ?? hoveredHotZone;
  const displayedHotZoneMouse = hoveredHotZonePinned !== null ? hotZoneMousePinned : hotZoneMouse;
  // Pinned heatmap-cell tooltip > live hover. Used for the gradient-mode
  // hover-dot + tooltip rendering.
  const displayedHoverPoint = pinnedHoverPoint ?? hoverPoint;

  return (
    <>
    <div
      className={`rounded-xl overflow-hidden bg-card border border-border flex flex-col${imgUrl ? ' cursor-pointer hover:border-primary/50 transition-colors' : ''}`}
      onClick={() => {
        // Anywhere on the tile (header, image, polygon, hot zone,
        // heatmap cell) zooms into the fullscreen modal. The tile is
        // too small to interact with tooltips meaningfully — pinning
        // and hovering are first-class in the modal, not here.
        clearAllPins();
        if (imgUrl) setZoomed(true);
      }}
    >
      <div className="px-3 py-1.5 text-xs font-medium text-muted-foreground border-b border-border truncate">
        {cameraName}
      </div>
      <div className="flex-1 flex items-center justify-center min-h-0 relative">
        {imgUrl && (
          <img
            ref={imgRef}
            src={imgUrl}
            alt={cameraName}
            className="w-full h-full object-contain"
            onLoad={() => {
              if (imgRef.current) {
                setImgDims({ w: imgRef.current.naturalWidth, h: imgRef.current.naturalHeight });
              }
            }}
            onError={() => setImgFailed(true)}
          />
        )}
        {/* Frame fetch genuinely failed (retries exhausted, or the
            <img> element couldn't load the URL). Shows a manual
            retry button so the user can recover without reloading
            the page. The polygons / heatmap underneath still render
            and stay interactive — only the button inside the
            placeholder accepts clicks. */}
        {frameFetchFailed && !imgUrl && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="bg-background/85 text-foreground border border-border rounded-lg px-3 py-2 text-xs shadow-sm backdrop-blur-sm flex items-center gap-2 max-w-[80%] pointer-events-auto">
              <CameraOff className="size-4 shrink-0 text-muted-foreground" />
              <span className="text-muted-foreground">{t('heatmap.camera.frameFetchFailed')}</span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); retryFrame(); }}
                className="ml-1 px-2 py-0.5 rounded-md border border-border hover:bg-muted transition-colors text-foreground"
              >
                {t('common.retry')}
              </button>
            </div>
          </div>
        )}
        {/* Per-layer loader badges + elapsed-seconds — only show loaders
            for what the *current mode* will actually display. Loaders
            include "· Ns" once we've been waiting > 3 s so a long cold
            compute (30-day window over a busy camera) doesn't look
            frozen.
            Progress bar: when we've got per-day progress data from the
            BE, fill the panel left-to-right based on completed_days /
            total_days. Falls back to a dim backdrop if no progress is
            being reported (warm cache hit, no per-day loop). */}
        {(() => {
          const anyLoading = showFrameLoader || showOverlaysLoader || showHeatmapLoader;
          if (!anyLoading) return null;
          // ETA from the first poll on, smoothed by blending observed
          // pace with a 5 s/day default until ≥5 days are sampled.
          const heatmapEta = computeHeatmapEta(heatmapProgress, elapsedHeatmap);
          return (
            <div className="absolute top-2 right-2 flex flex-col items-end gap-1 pointer-events-none">
              {showFrameLoader && (
                <LoaderChip
                  label={t('heatmap.camera.loadingFrame')}
                  elapsed={elapsedFrame}
                  withEta={false}
                />
              )}
              {showOverlaysLoader && (
                <LoaderChip
                  label={t('heatmap.camera.loadingOverlays')}
                  elapsed={elapsedOverlays}
                  fraction={overlaysProgressFraction || null}
                  eta={overlaysEta}
                />
              )}
              {showHeatmapLoader && (
                <LoaderChip
                  label={t(mode === 'hotzones' ? 'heatmap.camera.loadingHotZones' : 'heatmap.camera.loadingHeatmap')}
                  elapsed={elapsedHeatmap}
                  fraction={heatmapProgressFraction || null}
                  eta={heatmapEta}
                />
              )}
            </div>
          );
        })()}
        {/* Partial-coverage warning chip — yellow, top-right, expandable.
            We render the heatmap underneath even when coverage is bad
            so a quiet zone with sparse frames still produces a useful
            picture; the chip just makes the missing data legible.
            Camera-data property, so visible in every layer mode. */}
        {!densityLoading && densityData?.coverage
          && densityData.coverage.status === 'partial'
          && densityData.coverage.total_target_seconds > 0 && (
          <div className="absolute top-2 right-2 z-10">
            <WarningChip coverage={densityData.coverage} />
          </div>
        )}
        {!densityLoading && densityData?.coverage
          && densityData.coverage.status === 'no_data' && (
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 max-w-[80%] text-center">
            <div className="bg-background/90 border border-border rounded-md px-3 py-2 text-xs text-foreground shadow-md backdrop-blur-sm">
              <div className="font-medium mb-0.5">
                {t('heatmap.coverage.noDataTitle')}
              </div>
              <div className="text-muted-foreground text-[11px]">
                {t('heatmap.coverage.noDataHint')}
              </div>
            </div>
          </div>
        )}
        {imgDims && (
          <svg
            ref={svgRef}
            viewBox={`0 0 ${imgDims.w} ${imgDims.h}`}
            preserveAspectRatio="xMidYMid meet"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
            }}
          >
            <defs>
              {/* SVG <defs> ids are GLOBAL within the document. Multiple
                  tiles on one panel must each have a unique id, otherwise
                  every tile ends up clipping against the FIRST tile's
                  contour. Suffix with cameraId so each tile has its own
                  clip-path + filter. */}
              {cutoffPath && (
                <clipPath id={`heatmap-cutoff-clip-tile-${cameraId}`}>
                  <path d={cutoffPath} />
                </clipPath>
              )}
              <filter id={`heatmap-soft-tile-${cameraId}`} x="-2%" y="-2%" width="104%" height="104%">
                <feGaussianBlur stdDeviation={Math.max(0.8, imgDims.w * 0.0015)} />
              </filter>
            </defs>
            {/* Density gradient — filter (light blur) softens cell-step
                artifacts; clip-path at the 20-min cutoff contour cuts
                sharply at the boundary. */}
            {mode === 'heatmap' && gradientUrl && densityData && (
              <>
                <image
                  href={gradientUrl}
                  x={0}
                  y={0}
                  width={imgDims.w}
                  height={imgDims.h}
                  preserveAspectRatio="none"
                  filter={`url(#heatmap-soft-tile-${cameraId})`}
                  clipPath={cutoffPath ? `url(#heatmap-cutoff-clip-tile-${cameraId})` : undefined}
                  style={{ pointerEvents: 'none', imageRendering: 'auto' }}
                />
                {/* Transparent capture rect for hover sampling — picks up the
                    pointer over the density area and turns mouse position
                    into a (col, row) cell read. */}
                <rect
                  x={0}
                  y={0}
                  width={imgDims.w}
                  height={imgDims.h}
                  fill="transparent"
                  style={{ pointerEvents: 'all', cursor: 'none' }}
                  onPointerDown={(e) => {
                    lastPointerTypeRef.current = e.pointerType;
                    if (e.pointerType !== 'touch') return;
                    e.stopPropagation();
                    e.currentTarget.setPointerCapture(e.pointerId);
                    tapStartRef.current = { x: e.clientX, y: e.clientY };
                    const svg = svgRef.current;
                    if (!svg || !densityData) return;
                    const ctm = svg.getScreenCTM();
                    if (!ctm) return;
                    const pt = svg.createSVGPoint();
                    pt.x = e.clientX;
                    pt.y = e.clientY;
                    const local = pt.matrixTransform(ctm.inverse());
                    const { cols, rows, values } = densityData.grid;
                    const c = Math.max(0, Math.min(cols - 1, Math.floor((local.x / imgDims.w) * cols)));
                    const r = Math.max(0, Math.min(rows - 1, Math.floor((local.y / imgDims.h) * rows)));
                    const v = values[r * cols + c] ?? 0;
                    const cx = (c + 0.5) * (imgDims.w / cols);
                    const cy = (r + 0.5) * (imgDims.h / rows);
                    setHoverPoint({ gx: c, gy: r, cx, cy, raw: densityData.occupied_grid?.values?.[r * cols + c] ?? (v * densityData.max_density), clientX: e.clientX, clientY: e.clientY });
                  }}
                  onPointerMove={(e) => {
                    e.stopPropagation();
                    const svg = svgRef.current;
                    if (!svg || !densityData) return;
                    const ctm = svg.getScreenCTM();
                    if (!ctm) return;
                    const pt = svg.createSVGPoint();
                    pt.x = e.clientX;
                    pt.y = e.clientY;
                    const local = pt.matrixTransform(ctm.inverse());
                    const { cols, rows, values } = densityData.grid;
                    const c = Math.max(0, Math.min(cols - 1, Math.floor((local.x / imgDims.w) * cols)));
                    const r = Math.max(0, Math.min(rows - 1, Math.floor((local.y / imgDims.h) * rows)));
                    const v = values[r * cols + c] ?? 0;
                    const cx = (c + 0.5) * (imgDims.w / cols);
                    const cy = (r + 0.5) * (imgDims.h / rows);
                    // Always set hoverPoint so the cursor circle shows even
                    // over no-data cells; raw=0 hides the tooltip but keeps
                    // the dot visible (no flicker between system + custom
                    // cursor when crossing data boundaries).
                    setHoverPoint({ gx: c, gy: r, cx, cy, raw: densityData.occupied_grid?.values?.[r * cols + c] ?? (v * densityData.max_density), clientX: e.clientX, clientY: e.clientY });
                  }}
                  onPointerLeave={(e) => {
                    if (e.pointerType !== 'touch') {
                      setHoverPoint(null);
                    }
                  }}
                  onPointerCancel={() => {
                    tapStartRef.current = null;
                    setHoverPoint(null);
                  }}
                  onPointerUp={(e) => {
                    if (e.pointerType !== 'touch') return;
                    e.stopPropagation();
                    const start = tapStartRef.current;
                    tapStartRef.current = null;
                    setHoverPoint(null);
                    if (!start || !densityData) return;
                    const dx = e.clientX - start.x;
                    const dy = e.clientY - start.y;
                    const isTap = Math.hypot(dx, dy) < 10;
                    if (!isTap) return;
                    // Tap on the tile = open the modal (only when over a
                    // data cell so a tap on empty space doesn't yank
                    // users into fullscreen). The modal handles the
                    // "tap shows charts" gesture; this surface is too
                    // small to host the chart panel.
                    const svg = svgRef.current;
                    if (!svg) return;
                    const ctm = svg.getScreenCTM();
                    if (!ctm) return;
                    const pt = svg.createSVGPoint();
                    pt.x = e.clientX;
                    pt.y = e.clientY;
                    const local = pt.matrixTransform(ctm.inverse());
                    const { cols, rows, values } = densityData.grid;
                    const c = Math.max(0, Math.min(cols - 1, Math.floor((local.x / imgDims.w) * cols)));
                    const r = Math.max(0, Math.min(rows - 1, Math.floor((local.y / imgDims.h) * rows)));
                    const v = values[r * cols + c] ?? 0;
                    const raw = densityData.occupied_grid?.values?.[r * cols + c] ?? (v * densityData.max_density);
                    if (raw >= HEATMAP_MIN_HOURS_PER_DAY && imgUrl) {
                      setZoomed(true);
                    }
                  }}
                  onClick={() => {
                    // Suppress the synthesized click on touch — pin
                    // path lives in onPointerUp above.
                    if (lastPointerTypeRef.current === 'touch') return;
                    if (imgUrl) setZoomed(true);
                  }}
                />
                {/* Hover-dot — small fixed-size, dark + white halo for
                    contrast on any background, hovered cell's palette
                    color in the middle. */}
                {hoverPoint && densityData && (() => {
                  const hp = hoverPoint;
                  const peakRef = peakForColor || densityData.max_density || 1;
                  const overData = hp.raw >= HEATMAP_MIN_HOURS_PER_DAY;
                  const range = Math.max(1e-6, peakRef - HEATMAP_MIN_HOURS_PER_DAY);
                  const tNorm = overData ? Math.min(1, (hp.raw - HEATMAP_MIN_HOURS_PER_DAY) / range) : 0;
                  const tColor = 0.10 + 0.82 * Math.pow(tNorm, 0.5);
                  const [pr, pg, pb] = spectralRColor(tColor);
                  // Constant SCREEN-px cursor; see CameraZoomModal for rationale.
                  const vbRatio = displayedWidth > 0 ? imgDims.w / displayedWidth : 1;
                  const dotR = 7 * vbRatio;
                  const darkHaloR = 11 * vbRatio;
                  const whiteHaloR = 9 * vbRatio;
                  return (
                    <g style={{ pointerEvents: 'none' }}>
                      <circle cx={hp.cx} cy={hp.cy} r={darkHaloR} fill="rgba(0,0,0,0.7)" />
                      <circle cx={hp.cx} cy={hp.cy} r={whiteHaloR} fill="rgba(255,255,255,0.95)" />
                      <circle
                        cx={hp.cx} cy={hp.cy} r={dotR}
                        fill={overData ? `rgb(${pr},${pg},${pb})` : 'rgba(140,140,140,0.95)'}
                      />
                    </g>
                  );
                })()}
              </>
            )}
            {/* Hot-zone clusters — palette-colored blobs over the frame.
                Hover surfaces per-cluster stats (hours/day + avg-when-present). */}
            {mode === 'hotzones' && hotZones.map((zone, i) => {
              const isActive = displayedHotZoneIdx === i;
              return (
                <path
                  key={i}
                  d={zone.d}
                  fill={hotZoneFill(zone.data.occupied_hours_per_day, peakForColor, isActive)}
                  stroke="rgba(255,255,255,0.30)"
                  strokeWidth={isActive ? 1.0 : 0.6}
                  style={{ pointerEvents: 'all', cursor: 'pointer' }}
                  onMouseEnter={(e) => { e.stopPropagation(); setHoveredHotZone(i); setHotZoneMouse({ x: e.clientX, y: e.clientY }); }}
                  onMouseMove={(e) => { e.stopPropagation(); setHotZoneMouse({ x: e.clientX, y: e.clientY }); }}
                  onMouseLeave={() => { setHoveredHotZone(null); setHotZoneMouse(null); }}
                  onClick={() => { if (imgUrl) setZoomed(true); }}
                />
              );
            })}
            {/* Annotated polygons — only in zones mode. */}
            {mode === 'zones' && overlays.map((overlay) => {
              if (!overlay.points.length) return null;
              const pts = overlay.points
                .map(([x, y]) => `${x * imgDims.w},${y * imgDims.h}`)
                .join(' ');
              const isActive = overlay.id === displayedOverlayId;
              return (
                <polygon
                  key={overlay.id}
                  points={pts}
                  fill={isActive ? 'rgba(59,130,246,0.40)' : 'rgba(59,130,246,0.22)'}
                  stroke="#3b82f6"
                  strokeWidth={isActive ? 2 : 1}
                  style={{ pointerEvents: 'all', cursor: 'pointer' }}
                  onMouseEnter={(e) => { e.stopPropagation(); setHoveredId(overlay.id); setMousePos({ x: e.clientX, y: e.clientY }); }}
                  onMouseMove={(e) => { e.stopPropagation(); setMousePos({ x: e.clientX, y: e.clientY }); }}
                  onMouseLeave={() => { setHoveredId(null); setMousePos(null); }}
                  onClick={() => { if (imgUrl) setZoomed(true); }}
                />
              );
            })}
          </svg>
        )}
        {densityData && !densityLoading && mode !== 'zones' && (
          <Colorbar maxDensity={peakForColor || densityData.max_density} />
        )}
      </div>
    </div>
    {/* Polygon tooltip (zones mode). Pinned tooltip (= clicked) survives
        mouseleave; hover-only tooltip clears when the pointer leaves. Wraps
        long names to a second line instead of truncating. */}
    {displayedOverlay && displayedMousePos && (() => {
      const pos = clampTooltipPosition(displayedMousePos.x, displayedMousePos.y, 140, 80);
      return createPortal(
      <div
        style={{
          position: 'fixed',
          left: pos.left,
          top: pos.top,
          transform: pos.transform,
          pointerEvents: 'none',
          zIndex: 9999,
          maxWidth: 'min(85vw, 320px)',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
        }}
        className="bg-background/90 text-foreground border border-border rounded-lg px-3 py-1.5 text-xs shadow-md backdrop-blur-sm"
      >
        <div className="font-medium">
          {displayedOverlay.name ?? t('heatmap.unnamed.zone', { id: displayedOverlay.id })}
        </div>
        {(displayedOverlay.hoursPerDay > 0 || displayedOverlay.avgWhenOccupied > 0) && (
          <div className="text-muted-foreground">
            {displayedOverlay.hoursPerDay > 0
              ? t('heatmap.barchart.hoursPerDay', { hours: displayedOverlay.hoursPerDay.toFixed(1) })
              : ''}
            {displayedOverlay.hoursPerDay > 0 && displayedOverlay.avgWhenOccupied > 0 ? ' · ' : ''}
            {displayedOverlay.avgWhenOccupied > 0
              ? t('heatmap.barchart.avgWhenOccupied', { avg: displayedOverlay.avgWhenOccupied.toFixed(1) })
              : ''}
          </div>
        )}
      </div>,
      document.body,
    );
    })()}
    {/* Density tooltip — pinned > hovered. Wraps if narrow. */}
    {displayedHoverPoint && displayedHoverPoint.raw >= HEATMAP_MIN_HOURS_PER_DAY && (() => {
      const pos = clampTooltipPosition(displayedHoverPoint.clientX, displayedHoverPoint.clientY, 100, 50);
      return createPortal(
      <div
        style={{
          position: 'fixed',
          left: pos.left,
          top: pos.top,
          transform: pos.transform,
          pointerEvents: 'none',
          zIndex: 9999,
          maxWidth: 'min(85vw, 360px)',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
        }}
        className="bg-background/90 text-foreground border border-border rounded-lg px-2.5 py-1 text-xs shadow-md backdrop-blur-sm"
      >
        {formatHoursPerDay(displayedHoverPoint.raw, t)}
      </div>,
      document.body,
    );
    })()}
    {/* Hot zone tooltip — uses displayedHotZone* (pinned > hovered). */}
    {displayedHotZoneIdx !== null && displayedHotZoneIdx !== undefined && displayedHotZoneMouse && hotZones[displayedHotZoneIdx] && (() => {
      const pos = clampTooltipPosition(displayedHotZoneMouse.x, displayedHotZoneMouse.y, 160, 80);
      return createPortal(
      <div
        style={{
          position: 'fixed',
          left: pos.left,
          top: pos.top,
          transform: pos.transform,
          pointerEvents: 'none',
          zIndex: 9999,
          maxWidth: 'min(85vw, 360px)',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
        }}
        className="bg-background/90 text-foreground border border-border rounded-lg px-3 py-1.5 text-xs shadow-md backdrop-blur-sm"
      >
        <div className="font-medium">
          {t('heatmap.barchart.hoursPerDay', { hours: hotZones[displayedHotZoneIdx].data.occupied_hours_per_day.toFixed(1) })}
        </div>
        {hotZones[displayedHotZoneIdx].data.avg_when_occupied > 0 && (
          <div className="text-muted-foreground">
            {t('heatmap.barchart.avgWhenOccupied', { avg: hotZones[displayedHotZoneIdx].data.avg_when_occupied.toFixed(1) })}
          </div>
        )}
      </div>,
      document.body,
    );
    })()}
    {zoomed && imgUrl && createPortal(
      <CameraZoomModal
        cameraId={cameraId}
        imgUrl={imgUrl}
        cameraName={cameraName}
        overlays={overlays}
        overlaysLoading={overlaysLoading}
        overlaysProgressFraction={overlaysProgressFraction}
        overlaysEta={overlaysEta}
        fromMs={fromMs}
        toMs={toMs}
        weekdays={weekdays}
        densityData={densityData}
        mode={mode}
        globalPeak={peakForColor}
        onClose={() => setZoomed(false)}
      />,
      document.body,
    )}
    </>
  );
}

function BarChartSkeleton() {
  return (
    <div className="space-y-2 animate-pulse" aria-hidden>
      {[55, 80, 40, 65, 30].map((w, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="h-3 rounded bg-muted" style={{ width: '148px' }} />
          <div className="h-7 rounded-sm bg-muted/60" style={{ width: `${w}%` }} />
        </div>
      ))}
    </div>
  );
}

/** Tile-grid skeleton — fills the slot before the cameras list arrives so
 *  the panel doesn't flash empty while the zone is still loading. Rendered
 *  count + height match the real grid (2 cols, 280px rows). */
function CameraTileGridSkeleton({ count = 2 }: { count?: number }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" style={{ gridAutoRows: '280px' }} aria-hidden>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="relative rounded-lg border border-border bg-muted/40 overflow-hidden animate-pulse"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-muted/60 via-muted/30 to-muted/60" />
          <div className="absolute top-2 left-2 h-4 w-32 rounded bg-muted/70" />
          <div className="absolute bottom-2 right-2 h-3 w-16 rounded bg-muted/70" />
        </div>
      ))}
    </div>
  );
}

interface ZonePanelProps {
  zoneId: string;
  from: string;
  to: string;
  weekdays?: WeekdayFilter;
  workingHours?: ZoneWorkingHoursDto[];
  mode?: LayerMode;
}

function ZonePanel({
  zoneId, from, to, weekdays = 'all',
  workingHours = [], mode = DEFAULT_LAYER_MODE,
}: ZonePanelProps) {
  const { t, lang } = useT();
  const { search } = useLocation();
  const [activePolygonId, setActivePolygonId] = useState<number | null>(null);

  const { data, isLoading, error } = useSWR(
    ['seating-stats', zoneId, from, to, weekdays],
    () => getSeatingStats(zoneId, from, to, weekdays),
  );

  const { data: todData, isLoading: todLoading, error: todError } = useSWR(
    activePolygonId ? ['seating-tod', activePolygonId, from, to] : null,
    () => getSeatingTimeofday(activePolygonId!, from, to),
  );

  const { data: areasList, isLoading: areasLoading } = useSWR(
    ['areas-by-zone', zoneId],
    async () => {
      const res = await listAreasByZone(zoneId);
      return res.data.results;
    },
    { revalidateOnFocus: false },
  );

  const { data: allZoneCameras, isLoading: allZoneCamerasLoading } = useSWR(
    ['cameras-by-zone', zoneId],
    async () => (await listCameras(1000, zoneId)).data.results,
    { revalidateOnFocus: false },
  );

  // True while either the zone's polygons or per-area geometry is still
  // loading — used by CameraTile to render a "Зоны…" badge.
  const overlaysLoading = isLoading || areasLoading;
  // Per-day-loop progress for the seating-stats compute (only the
  // seating-stats fetch has a per-day loop; areasLoading is fast).
  // Computed once at the panel level since it's shared across every
  // tile. Same hook + ETA logic the heatmap loader uses, so the
  // zones-mode chip renders an identical bar + ETA.
  const seatingProgress = useSeatingProgress(isLoading, zoneId, from, to, weekdays);
  const elapsedSeatingShared = useElapsedSeconds(isLoading);
  const overlaysProgressFraction = seatingProgress && seatingProgress.total_days > 0
    ? seatingProgress.completed_days / seatingProgress.total_days
    : 0;
  const overlaysEta = computeHeatmapEta(seatingProgress, elapsedSeatingShared);

  const polygons = data?.polygons ?? [];
  const seatingPolygons = polygons.filter((p) => p.is_table);
  // For the camera-tile overlay we also include "Рабочая зона" (CheckoutZone
  // type=STAFF) — same blue rendering as seating polygons. The bar chart
  // and insights stay on tables only since they have meaningful occupancy
  // semantics.
  const overlayPolygons = polygons.filter((p) => p.is_table || p.is_staff_zone);
  const hasData = seatingPolygons.length > 0;

  const fromMs = toUnixMs(from);
  const toMs = toUnixMs(to) + 86_399_999;

  // Show every active camera in the zone (active = configured for use, not
  // necessarily online). listCameras is the primary source so cameras don't
  // disappear when seating-stats resolves and it turns out some have no
  // tables yet. Polygon-derived names are the fallback while listCameras is
  // in flight; we trust polygons came from active cameras since detection
  // pipelines only run on those.
  const cameras = useMemo(() => {
    if (allZoneCameras && allZoneCameras.length > 0) {
      return allZoneCameras
        .filter((c) => c.active)
        .map((c) => ({ id: Number(c.id), name: c.name }));
    }
    if (polygons.length > 0) {
      const map = new Map<number, string>();
      for (const p of polygons) {
        if (!map.has(p.camera_id)) {
          map.set(p.camera_id, p.camera_name ?? t('heatmap.unnamed.camera', { id: p.camera_id }));
        }
      }
      return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
    }
    return [];
  }, [polygons, allZoneCameras, t]);

  const { label: whLabel, avgHoursPerDay } = useMemo(
    () => parseWorkingHours(workingHours, lang),
    [workingHours, lang],
  );

  // Cap occupied_hours_per_day at the zone's working hours. Without this,
  // after-hours detection noise (shadows, reflections, transient false-positive
  // detections) can push the metric above the physical maximum — e.g. zone 19
  // is 12 h/day open but raw values reach 21 h/day. Clamping is a soft fix;
  // the principled fix would be SQL-side filtering by working-hours timestamps.
  const clampHours = (h: number): number =>
    avgHoursPerDay && avgHoursPerDay > 0 ? Math.min(h, avgHoursPerDay) : h;

  const chartData = seatingPolygons
    .slice()
    .sort((a, b) => b.occupied_hours_per_day - a.occupied_hours_per_day)
    .slice(0, 12)
    .map((p) => {
      const displayName = p.polygon_name ?? t('heatmap.unnamed.zone', { id: p.polygon_id });
      const cappedHours = clampHours(p.occupied_hours_per_day);
      return {
        name: displayName.length > 22 ? displayName.slice(0, 22) + '…' : displayName,
        value: cappedHours,
        id: p.polygon_id,
        hoursPerDay: cappedHours,
        avgWhenOccupied: p.avg_when_occupied,
        groupSizeMinutes: p.group_size_minutes,
      };
    });

  const areasByCamera = useMemo(() => {
    const result = new Map<number, PolygonOverlay[]>();
    if (!areasList) return result;
    for (const area of areasList) {
      if (!area.direction_vectors?.length) continue;
      const areaId = Number(area.id);
      const poly = overlayPolygons.find((p) => p.polygon_id === areaId);
      if (!poly) continue;
      const camId = Number(area.camera);
      if (!result.has(camId)) result.set(camId, []);
      result.get(camId)!.push({
        id: areaId,
        name: poly.polygon_name,
        points: area.direction_vectors,
        avg: poly.avg_occupancy,
        avgWhenOccupied: poly.avg_when_occupied,
        hoursPerDay: clampHours(poly.occupied_hours_per_day),
      });
    }
    return result;
  }, [areasList, overlayPolygons, clampHours]);

  const insight = data ? topZoneInsight(seatingPolygons, t) : null;
  // Treat seating-stats failure the same as "no annotated tables": still show
  // the per-camera heatmap, just hide the bar chart and surface the zoning
  // CTA. The heatmap density grid is independent of seating-stats.
  const showSeatingCta = !isLoading && !hasData;

  // Cross-camera color reference. Each tile reports its max_density when its
  // density grid loads; the panel-wide max becomes the colormap reference so
  // identical hours/day across cameras renders as the same color.
  const [cameraPeaks, setCameraPeaks] = useState<Record<number, number>>({});
  const handlePeak = React.useCallback((cameraId: number, peak: number) => {
    setCameraPeaks((prev) => (prev[cameraId] === peak ? prev : { ...prev, [cameraId]: peak }));
  }, []);
  const globalPeak = useMemo(() => {
    const vals = Object.values(cameraPeaks).filter((v) => v > 0);
    return vals.length ? Math.max(...vals) : 0;
  }, [cameraPeaks]);

  // Track coverage status per camera. Tiles with non-ok coverage (chronic
  // partial / no data) sink to the bottom of the grid so the user sees
  // usable heatmaps first.
  const [cameraCoverage, setCameraCoverage] = useState<Record<number, string | null>>({});
  const handleCoverage = React.useCallback((cameraId: number, status: string | null) => {
    setCameraCoverage((prev) => (prev[cameraId] === status ? prev : { ...prev, [cameraId]: status }));
  }, []);
  const orderedCameras = useMemo(() => {
    // Stable demotion: ok / unknown stay; non-ok go after.
    const score = (id: number) => {
      const s = cameraCoverage[id];
      return s && s !== 'ok' ? 1 : 0;
    };
    return [...cameras].sort((a, b) => score(a.id) - score(b.id));
  }, [cameras, cameraCoverage]);

  return (
    <div className="flex flex-col flex-1 gap-5">
      {whLabel && (
        <p className="text-xs text-muted-foreground">
          {t('heatmap.workingHours.label', { hours: whLabel })}
        </p>
      )}

      {cameras.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" style={{ gridAutoRows: '280px' }}>
          {orderedCameras.map((cam) => (
            <CameraTile
              key={cam.id}
              cameraId={cam.id}
              cameraName={cam.name}
              fromMs={fromMs}
              toMs={toMs}
              weekdays={weekdays}
              overlays={areasByCamera.get(cam.id) ?? []}
              overlaysLoading={overlaysLoading}
              overlaysProgressFraction={overlaysProgressFraction}
              overlaysEta={overlaysEta}
              mode={mode}
              globalPeak={globalPeak || undefined}
              onPeakLoaded={handlePeak}
              onCoverageReported={handleCoverage}
            />
          ))}
        </div>
      ) : (
        /* Fill the slot before the cameras list resolves; otherwise the
           page shows a blank gap during the initial zone fetch. */
        (allZoneCamerasLoading || isLoading) && <CameraTileGridSkeleton />
      )}

      {showSeatingCta && mode === 'zones' && (
        <div className="rounded-xl border border-dashed border-border p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <p className="flex-1 text-sm text-muted-foreground">{t('heatmap.seatingCta.title')}</p>
          <Link
            to={{ pathname: '/management/zoning', search }}
            className="shrink-0 inline-flex items-center px-4 py-2 text-sm rounded-lg border border-border hover:bg-muted transition-colors"
          >
            {t('heatmap.seatingCta.button')}
          </Link>
        </div>
      )}

      {insight && mode === 'zones' && (
        <p className="text-sm text-muted-foreground italic px-1">{insight}</p>
      )}

      {hasData && mode === 'zones' && (
      <>
      <div className="mt-auto">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
          {t('heatmap.barchart.title')}
        </p>
        {isLoading ? (
          <BarChartSkeleton />
        ) : (
          <div className="[&_*:focus]:outline-none [&_*:focus-visible]:outline-none">
            <ResponsiveContainer width="100%" height={Math.max(chartData.length * 34, 60)}>
              <BarChart
            accessibilityLayer={false}
                layout="vertical"
                data={chartData}
                margin={{ left: 0, right: 60, top: 0, bottom: 0 }}
              >
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" width={148} tick={{ fontSize: 12 }} />
                <Tooltip
            isAnimationActive={false}
                  content={(props) => {
                    if (!props.active || !props.payload?.length) return null;
                    const d = props.payload[0].payload as {
                      hoursPerDay: number;
                      avgWhenOccupied: number;
                      groupSizeMinutes: Record<string, number>;
                    };
                    const histEntries = Object.entries(d.groupSizeMinutes ?? {})
                      .filter(([, m]) => m > 0)
                      .sort((a, b) => {
                        const an = a[0] === '8+' ? 8.5 : Number(a[0]);
                        const bn = b[0] === '8+' ? 8.5 : Number(b[0]);
                        return an - bn;
                      });
                    const totalMin = histEntries.reduce((s, [, m]) => s + m, 0);
                    return (
                      <div className="bg-background text-foreground border border-border rounded-lg px-3 py-2 text-xs shadow-md">
                        {d.hoursPerDay > 0 && (
                          <p className="metric-value font-medium text-foreground">
                            {t('heatmap.barchart.hoursPerDay', { hours: d.hoursPerDay.toFixed(1) })}
                          </p>
                        )}
                        {d.avgWhenOccupied > 0 && (
                          <p className="text-muted-foreground">
                            {t('heatmap.barchart.avgWhenOccupied', { avg: d.avgWhenOccupied.toFixed(1) })}
                          </p>
                        )}
                        {histEntries.length > 0 && totalMin > 0 && (
                          <div className="mt-2 pt-1.5 border-t border-border/60">
                            <p className="text-muted-foreground mb-1.5">{t('heatmap.barchart.groupSizeHistogram')}</p>
                            <div className="flex items-end gap-1 h-12">
                              {histEntries.map(([size, minutes]) => {
                                const pct = (minutes / totalMin) * 100;
                                const barH = Math.max(2, (pct / 100) * 36);
                                return (
                                  <div key={size} className="flex flex-col items-center gap-0.5" style={{ minWidth: '18px' }}>
                                    <span className="text-[9px] text-foreground/80 leading-none">{Math.round(pct)}%</span>
                                    <div
                                      className="bg-primary/70 rounded-sm w-3"
                                      style={{ height: `${barH}px` }}
                                    />
                                    <span className="text-[9px] text-muted-foreground leading-none">{size}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  }}
                />
                <Bar
                  dataKey="value"
                  radius={[0, 4, 4, 0]}
                  cursor="pointer"
                  onClick={(entry) => {
                    const id = (entry as unknown as { id: number }).id;
                    setActivePolygonId(activePolygonId === id ? null : id);
                  }}
                >
                  {chartData.map((entry) => (
                    <Cell
                      key={entry.id}
                      fill={entry.id === activePolygonId ? '#2563eb' : '#60a5fa'}
                    />
                  ))}
                  <LabelList
                    dataKey="hoursPerDay"
                    position="right"
                    formatter={(v: unknown) => t('heatmap.barchart.hoursPerDay', { hours: (v as number).toFixed(1) })}
                    style={{ fontSize: 11, fill: 'var(--color-muted-foreground, #64748b)' }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            {activePolygonId === null && (
              <p className="text-xs text-muted-foreground mt-1">{t('heatmap.barchart.hint')}</p>
            )}
          </div>
        )}
      </div>

      {!isLoading && activePolygonId !== null && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            {t('heatmap.tod.title')} — {polygons.find((p) => p.polygon_id === activePolygonId)?.polygon_name ?? t('heatmap.unnamed.zone', { id: activePolygonId })}
          </p>
          {todError && !todData ? (
            <ServerStatusNotice variant="card" error={todError} />
          ) : todLoading || !todData?.matrix ? (
            <div className="h-24 flex items-center justify-center text-xs text-muted-foreground">
              {t('common.loading')}
            </div>
          ) : (
            <TODGrid matrix={todData.matrix} />
          )}
        </div>
      )}
      </>
      )}
    </div>
  );
}

// v2 — schema changed from { frame, zones, heatmap: bool } to single mode.
// Old key 'heatmap-layers-v1' is left to garbage-collect itself; no migration
// (the page is always rendered with the new schema after this deploy).
const LAYER_MODE_STORAGE_KEY = 'heatmap-layer-mode-v2';

function readStoredLayerMode(): LayerMode {
  try {
    const raw = localStorage.getItem(LAYER_MODE_STORAGE_KEY);
    if (raw && (ALL_LAYER_MODES as string[]).includes(raw)) return raw as LayerMode;
  } catch {
    /* storage may be disabled */
  }
  return DEFAULT_LAYER_MODE;
}

export default function HeatmapPage() {
  const { t } = useT();
  const { zoneId, zones } = useCurrentZone();
  const location = useLocation();
  // Floorplan upload is a superuser-only operation for now (the
  // upload pipeline isn't ready for general operator self-serve).
  const isAdmin = useCan('global.admin');
  const canSeePlanCta = isAdmin;
  const availableLayerModes = isAdmin ? ALL_LAYER_MODES : PUBLIC_LAYER_MODES;

  // Persist filters in URL search params so a refresh / shared link
  // restores the user's view. Period + compare state + selected zone-B
  // live in the URL; mode keeps its existing localStorage fallback so
  // users default to the layer they last looked at across all branches.
  // Reading from URL on first render means we don't need a separate
  // hydration effect — the initial state IS the URL state.
  const [searchParams, setSearchParams] = useSearchParams();
  useRememberFilters(['period', 'mode', 'compare', 'zoneB', 'periodB', 'modeB']);
  const parsePeriod = (raw: string | null, fallback: Period): Period =>
    ALL_PERIODS.includes(raw as Period) ? (raw as Period) : fallback;
  const parseMode = (raw: string | null, fallback: LayerMode): LayerMode =>
    raw && (ALL_LAYER_MODES as readonly string[]).includes(raw)
      ? (raw as LayerMode)
      : fallback;
  const initialPeriod = parsePeriod(searchParams.get('period'), '7d');
  const initialCompare = searchParams.get('compare') === '1';
  const initialZoneB = searchParams.get('zoneB') ?? '';
  const initialMode = parseMode(searchParams.get('mode'), readStoredLayerMode());
  // Pane-B overrides — independent period + mode so users can compare,
  // e.g., "weekends here" vs "workdays at the same branch", or this
  // branch's heatmap vs another's zones table. Defaults to A's value
  // so the user starts at the today-equivalent compare and only
  // diverges when they actively change something.
  const initialPeriodB = parsePeriod(searchParams.get('periodB'), initialPeriod);
  const initialModeB = parseMode(searchParams.get('modeB'), initialMode);
  const [period, setPeriod] = useState<Period>(initialPeriod);
  const [compareMode, setCompareMode] = useState<boolean>(initialCompare);
  const [zoneBId, setZoneBId] = useState<string>(initialZoneB);
  const [mode, setMode] = useState<LayerMode>(initialMode);
  const [periodB, setPeriodB] = useState<Period>(initialPeriodB);
  const [modeB, setModeB] = useState<LayerMode>(initialModeB);

  // Mirror filter pieces into the URL whenever they change. periodB /
  // modeB only land in the URL when they DIFFER from pane A so the
  // typical "same period both sides" link stays clean.
  // `replace: true` keeps browser history free of per-toggle entries.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (period === '7d') next.delete('period'); else next.set('period', period);
    if (compareMode) next.set('compare', '1'); else next.delete('compare');
    // zoneB / periodB / modeB only land in the URL when comparing.
    // Exiting compare mode strips them so a refresh doesn't half-
    // restore compare state (a user reported zoneB persisting after
    // exit on 2026-05-09, which is exactly that bug).
    if (compareMode && zoneBId) next.set('zoneB', zoneBId); else next.delete('zoneB');
    if (mode === DEFAULT_LAYER_MODE) next.delete('mode'); else next.set('mode', mode);
    if (compareMode && periodB !== period) next.set('periodB', periodB);
    else next.delete('periodB');
    if (compareMode && modeB !== mode) next.set('modeB', modeB);
    else next.delete('modeB');
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [period, compareMode, zoneBId, mode, periodB, modeB, searchParams, setSearchParams]);

  // Snap pane-B mode into the user's allowed set when permissions change.
  useEffect(() => {
    if (!availableLayerModes.includes(modeB)) {
      setModeB(availableLayerModes[0]);
    }
  }, [availableLayerModes, modeB]);

  // Snap mode back into the user's allowed set. Catches the case where a
  // regular user has 'hotzones' persisted in localStorage from a session
  // when the role was different.
  useEffect(() => {
    if (!availableLayerModes.includes(mode)) {
      setMode(availableLayerModes[0]);
    }
  }, [availableLayerModes, mode]);
  const handleModeChange = React.useCallback((next: LayerMode) => {
    setMode(next);
    try { localStorage.setItem(LAYER_MODE_STORAGE_KEY, next); } catch { /* storage may be disabled */ }
  }, []);

  const { from, to } = useMemo(() => periodDates(period), [period]);
  const weekdaysFilter = useMemo(() => periodWeekdayFilter(period), [period]);
  const { from: fromB, to: toB } = useMemo(() => periodDates(periodB), [periodB]);
  const weekdaysFilterB = useMemo(() => periodWeekdayFilter(periodB), [periodB]);

  // Compare's branch list mirrors the global BranchSelector: only
  // retail-style zones (RETAIL_BRANCH / COFFEESHOP / SHOP / CANTEEN
  // and zones without a type) — no service / system zones that
  // aren't selectable as the main branch elsewhere. Includes the
  // current zone so users can compare e.g. "weekends here" vs
  // "workdays here" without picking a different branch.
  const otherZones = useMemo(
    () => zones.filter((z) => String(z.id) !== String(zoneId)),
    [zones, zoneId],
  );
  const allZonesForCompare = useMemo(
    () => [...zones]
      .filter((z) => !z.type || COMPARE_RETAIL_TYPES.includes(z.type as ZoneType))
      .sort((a, b) =>
        (a.humanreadable_name ?? '').localeCompare(b.humanreadable_name ?? '')),
    [zones],
  );

  const resolvedZoneB = zoneBId
    || (otherZones[0] ? String(otherZones[0].id) : (zoneId ? String(zoneId) : ''));

  if (!zoneId) {
    return <BranchRequiredState />;
  }

  return (
    <div className="space-y-4">
      {/* Rendered as the Тепловая карта tab of Статистика — no page header of
          its own. Single mode uses the FULL width; compare splits it in two
          labeled halves. The compare entry/exit sits inline with pane A's
          pickers so it reads as part of the controls row. */}
      <div className={`grid gap-6 ${compareMode ? 'grid-cols-2' : 'grid-cols-1'}`}>
        {/* Pane A — current branch. Header has period + mode pickers
            and (in compare mode) the branch label so the side reads
            symmetric to pane B. */}
        <div className="flex flex-col">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {compareMode && (
              <span className="text-sm font-medium text-foreground mr-1">
                {zones.find((z) => String(z.id) === String(zoneId))?.humanreadable_name ?? t('heatmap.thisZone')}
              </span>
            )}
            <PeriodPicker value={period} onChange={setPeriod} t={t} />
            <LayerSwitcher mode={mode} onChange={handleModeChange} availableModes={availableLayerModes} />
            {compareMode ? (
              <button
                onClick={() => setCompareMode(false)}
                className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-border hover:bg-muted transition-colors"
              >
                <X className="size-3.5" />
                {t('heatmap.compareExit')}
              </button>
            ) : (
              otherZones.length > 0 && (
                <button
                  onClick={() => setCompareMode(true)}
                  className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-dashed border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  {t('heatmap.compareOn')}
                </button>
              )
            )}
          </div>
          <ZonePanel
            zoneId={zoneId}
            from={from}
            to={to}
            weekdays={weekdaysFilter}
            workingHours={zones.find((z) => String(z.id) === String(zoneId))?.working_hours ?? []}
            mode={mode}
          />
        </div>

        {/* Pane B — compare branch. Same shape as A's header plus a
            branch dropdown. Independent period + mode so you can
            compare any (branch, period, mode) tuple against any
            other. */}
        {compareMode && (
          <div className="flex flex-col">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <ZoneDropdown
                zones={allZonesForCompare}
                value={resolvedZoneB}
                onChange={setZoneBId}
              />
              <PeriodPicker value={periodB} onChange={setPeriodB} t={t} />
              <LayerSwitcher mode={modeB} onChange={setModeB} availableModes={availableLayerModes} />
            </div>
            {resolvedZoneB ? (
              <ZonePanel
                zoneId={resolvedZoneB}
                from={fromB}
                to={toB}
                weekdays={weekdaysFilterB}
                workingHours={zones.find((z) => String(z.id) === resolvedZoneB)?.working_hours ?? []}
                mode={modeB}
              />
            ) : (
              <p className="text-sm text-muted-foreground">{t('heatmap.compareHint')}</p>
            )}
          </div>
        )}
      </div>

      {(mode === 'heatmap' || mode === 'hotzones') && canSeePlanCta && !zones.find((z) => String(z.id) === String(zoneId))?.plan_image && (
        <div className="rounded-xl border border-dashed border-border p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <p className="flex-1 text-sm text-muted-foreground">{t('heatmap.planCta.title')}</p>
          <Link
            to={{ pathname: '/management/general', search: location.search }}
            className="shrink-0 inline-flex items-center px-4 py-2 text-sm rounded-lg border border-border hover:bg-muted transition-colors"
          >
            {t('heatmap.planCta.button')}
          </Link>
        </div>
      )}
    </div>
  );
}
